import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  ButtonGroup,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import FreeBreakfastIcon from '@mui/icons-material/FreeBreakfast';
import SpaIcon from '@mui/icons-material/Spa';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { jsPDF } from 'jspdf';
import { API_BASE_URL } from '../config';
import { authenticatedFetch, apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import { authaltFetch, fetchGuestTasks } from '../services/authaltApi';

// Atmantan brand colors
const ATMANTAN_GOLD = '#C7AE6E';
const ATMANTAN_DARK_GOLD = '#B09858';

const getFirstNonEmpty = (obj, keys) => {
  for (const key of keys) {
    const val = obj?.[key];
    if (val != null && String(val).trim() !== '') return String(val).trim();
  }
  return '';
};

const toISO = (value) => {
  const str = String(value || '').trim();
  if (!str) return '';
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const m = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})$/);
  if (m) {
    const d = String(m[1]).padStart(2, '0');
    const mo = String(m[2]).padStart(2, '0');
    const y = String(m[3]).length === 2 ? `20${m[3]}` : String(m[3]);
    return `${y}-${mo}-${d}`;
  }
  const t = Date.parse(str);
  if (!Number.isNaN(t)) return new Date(t).toISOString().slice(0, 10);
  return '';
};

const addDaysISO = (iso, offset) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + offset);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

const buildDateRangeISO = (startISO, endISO) => {
  if (!startISO || !endISO || startISO > endISO) return [];
  const days = [];
  let current = startISO;
  while (current <= endISO) {
    days.push(current);
    current = addDaysISO(current, 1);
  }
  return days;
};

const defaultForwardDateRange = () => {
  const today = new Date();
  const days = [];
  for (let i = 0; i <= 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    days.push(d.toISOString().slice(0, 10));
  }
  return days;
};

const STANDARD_DAILY_MEALS = [
  { startTime: '05:30', endTime: '06:00', treatment: 'WAKE UP', location: 'Room', room: 'Room', isStandard: true },
  { startTime: '07:00', endTime: '09:00', treatment: 'BREAKFAST', location: 'Restaurant', room: 'Restaurant', isStandard: true },
  { startTime: '10:30', endTime: '11:30', treatment: 'MID MORNING', location: 'Shakthi Wellness', room: 'Shakthi Wellness', isStandard: true },
  { startTime: '12:00', endTime: '14:30', treatment: 'LUNCH', location: 'Restaurant', room: 'Restaurant', isStandard: true },
  { startTime: '16:30', endTime: '17:30', treatment: 'EVENING', location: 'Shakthi Wellness', room: 'Shakthi Wellness', isStandard: true },
  { startTime: '19:00', endTime: '21:00', treatment: 'DINNER', location: 'Restaurant', room: 'Restaurant', isStandard: true }
];

const GENERAL_INSTRUCTIONS = [
  'Choose classes that suit your fitness level (★ Beginner — ★★ Intermediate — ★★★ Advanced). All classes are marked with stars as per their competence level.',
  'Report 5 minutes early to all group activities so you can set up mats, follow initial instructions, warm up, and get the full benefit of the session.',
  'Comfortable fitness gear is mandatory for all fitness activities — indoor sports shoes, track pants/sports trousers, and sports T-shirt.',
  'Personal Kriya kits are available for purchase at Wellness.',
  'Please carry the water bottles provided in your room for all activities so you stay hydrated.',
  'Management reserves the right to cancel outdoor activities subject to weather conditions.'
];

const WELLNESS_GUIDELINES = [
  'Kindly respect our cancellation policy as detailed in the Wellness etiquette.',
  'Please report on time or before to your Wellness appointment time as per the orientation guidelines. Should you arrive late, your treatment will be accommodated subject to subsequent bookings.',
  'The Wellness operating hours are from 7:00 am - 8 pm.',
  'Please arrive promptly for group activities and do not enter after five minutes of the start time of the class.',
  'Please wear appropriate clothing & footwear for the Gym/Pool/Outdoor activities or treatments.',
  'Please carry the water bottles provided in the room for all the activities so that you stay hydrated.',
  'Management reserves the right to cancel outdoor activities subject to weather conditions.',
  'Mid-day and mid-evening prescribed drinks will be served at the juice bar inside Shakthi Wellness. The serving hours are from 10:30 AM to 11:30 AM for mid-day drinks and from 4:30 PM to 5:30 PM for mid-evening drinks.'
];

const formatDisplayTreatmentName = (name) => {
  if (!name) return '';
  return String(name)
    .replace(/\(G\)/gi, '(Group)')
    .replace(/\s+\(g\)\s*$/i, ' (Group)');
};

const isDoctorConsultation = (treatmentName) => {
  const name = (treatmentName || '').toLowerCase();
  return name.includes('meet the doctor')
    || name.includes('doctor consultation')
    || name.includes('consultation with doctor')
    || (name.includes('doctor') && name.includes('consult'));
};

const isStandardMealName = (treatmentName) => {
  const name = (treatmentName || '').toLowerCase();
  return STANDARD_DAILY_MEALS.some((meal) => name.includes(meal.treatment.toLowerCase()))
    || name.includes('mid-day')
    || name.includes('mid evening')
    || name.includes('mid-evening');
};

const getGuestTaskLabel = (item) => {
  if (typeof item === 'string') return item.trim();
  return String(item?.task || item?.task_name || item?.taskName || '').trim();
};

const getGuestTaskDayNumber = (item) => {
  if (typeof item !== 'object' || !item) return null;
  const raw = item.day ?? item.day_number ?? item.dayNumber ?? item.day_no
    ?? item.task_day ?? item.schedule_day ?? item.package_day;
  if (raw == null || raw === '') return null;
  const str = String(raw).trim();
  const dayMatch = str.match(/day\s*(\d+)/i);
  if (dayMatch) return parseInt(dayMatch[1], 10);
  const n = parseInt(str, 10);
  return Number.isFinite(n) ? n : null;
};

const filterGuestTasksForDay = (guestTasks, dayNum) => {
  if (!guestTasks?.length) return [];
  if (!dayNum) return guestTasks;
  const hasDayField = guestTasks.some((item) => getGuestTaskDayNumber(item) != null);
  if (!hasDayField) return guestTasks;
  return guestTasks.filter((item) => getGuestTaskDayNumber(item) === dayNum);
};

const getUniqueGuestTaskLabels = (guestTasks) => {
  const seen = new Set();
  return (guestTasks || [])
    .map(getGuestTaskLabel)
    .filter((task) => {
      const key = task.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

// Font loading cache
let robotoFontsLoaded = false;
let robotoFontBase64 = null;
let robotoBoldFontBase64 = null;

// Helper function to load font file and convert to base64
const loadFontAsBase64 = async (fontPath) => {
  try {
    const response = await fetch(fontPath);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Error loading font:', error);
    return null;
  }
};

// Function to add Roboto fonts to jsPDF
const addRobotoFonts = async (doc) => {
  if (!robotoFontsLoaded) {
    try {
      console.log('Loading Roboto fonts...');
      
      // Load Roboto Regular
      if (!robotoFontBase64) {
        robotoFontBase64 = await loadFontAsBase64('/fonts/Roboto/static/Roboto-Regular.ttf');
      }
      
      // Load Roboto Bold
      if (!robotoBoldFontBase64) {
        robotoBoldFontBase64 = await loadFontAsBase64('/fonts/Roboto/static/Roboto-Bold.ttf');
      }
      
      if (robotoFontBase64) {
        doc.addFileToVFS('Roboto-Regular.ttf', robotoFontBase64);
        doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
        console.log('Roboto Regular loaded');
      }
      
      if (robotoBoldFontBase64) {
        doc.addFileToVFS('Roboto-Bold.ttf', robotoBoldFontBase64);
        doc.addFont('Roboto-Bold.ttf', 'Roboto', 'bold');
        console.log('Roboto Bold loaded');
      }
      
      robotoFontsLoaded = true;
    } catch (error) {
      console.error('Error adding Roboto fonts:', error);
      // Fallback to Helvetica
      doc.setFont('Roboto');
    }
  }
  
  // Set Roboto as default font
  try {
    doc.setFont('Roboto', 'normal');
  } catch (error) {
    console.error('Error setting Roboto font:', error);
    doc.setFont('Roboto');
  }
};

const ItineraryModal = ({ open, onClose, guestId, guestName, bookingId: bookingIdProp, embedded = false }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState([]);
  const [guestInfo, setGuestInfo] = useState(null);
  const [selectedDay, setSelectedDay] = useState('all'); // 'all' or specific day number
  const [allActivities, setAllActivities] = useState([]); // All available activities for the date range
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'card'
  const [roomsData, setRoomsData] = useState({}); // Store room name -> location mapping
  const [activityLayout, setActivityLayout] = useState('grid'); // 'grid' or 'timeslot'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [treatmentDescriptions, setTreatmentDescriptions] = useState({});
  const [guestTasks, setGuestTasks] = useState([]);

  // Fetch rooms configuration to get room locations
  const fetchRoomsData = async () => {
    try {
      const response = await apiGet('/api/config');
      if (response.ok) {
        const data = await response.json();
        setRoomsData(data.rooms || {});
      }
    } catch (error) {
      console.error('Error fetching rooms data:', error);
    }
  };

  const fetchTreatmentDescriptions = async () => {
    try {
      const response = await apiGet('/api/admin/treatments');
      if (!response.ok) return;
      const treatments = await response.json();
      const descriptionMap = {};
      (Array.isArray(treatments) ? treatments : []).forEach((treatment) => {
        const name = (treatment.name || '').toLowerCase();
        const description = treatment.short_description || treatment.description;
        if (name && description) {
          descriptionMap[name] = description;
        }
      });
      setTreatmentDescriptions(descriptionMap);
    } catch (error) {
      console.error('Error fetching treatment descriptions:', error);
    }
  };

  useEffect(() => {
    if (!open) {
      setDateRange([]);
      setGuestInfo(null);
      setCustomStartDate('');
      setCustomEndDate('');
      return;
    }
    if (guestId || guestName) {
      fetchRoomsData();
      fetchTreatmentDescriptions();
      fetchGuestInfo();
    }
  }, [open, guestId, guestName]);

  // Fetch itinerary after rooms data is loaded
  useEffect(() => {
    if (open && (guestId || guestName) && Object.keys(roomsData).length > 0 && dateRange.length > 0) {
      fetchItinerary();
      fetchAllActivities();
    }
  }, [open, guestId, guestName, roomsData, dateRange]);

  // Use guest stay dates for itinerary range (not today)
  useEffect(() => {
    if (!guestInfo) return;

    const checkIn = toISO(getFirstNonEmpty(guestInfo, ['checkIn', 'checkin', 'check_in', 'startDate', 'arrivalDate']));
    const checkOut = toISO(getFirstNonEmpty(guestInfo, ['checkOut', 'checkout', 'check_out', 'endDate', 'departureDate']));

    if (checkIn && checkOut) {
      setCustomStartDate(checkIn);
      setCustomEndDate(checkOut);
      setDateRange(buildDateRangeISO(checkIn, checkOut));
      return;
    }

    setDateRange(defaultForwardDateRange());
  }, [guestInfo]);

  const doctorRecommendations = useMemo(
    () => getUniqueGuestTaskLabels(guestTasks),
    [guestTasks]
  );

  const loadGuestTasks = async (resolvedGuestId, resolvedBookingId) => {
    if (!resolvedGuestId || !resolvedBookingId) {
      setGuestTasks([]);
      return;
    }

    try {
      const tasks = await fetchGuestTasks(resolvedGuestId, resolvedBookingId);
      setGuestTasks(tasks);
    } catch (error) {
      console.error('Error loading guest tasks:', error);
      setGuestTasks([]);
    }
  };

  useEffect(() => {
    if (!open) {
      setGuestTasks([]);
      return;
    }

    if (guestInfo?.guestId && guestInfo?.bookingId) {
      loadGuestTasks(guestInfo.guestId, guestInfo.bookingId);
    }
  }, [open, guestInfo?.guestId, guestInfo?.bookingId]);

  // Update date range when custom dates change
  const handleDateRangeChange = () => {
    if (customStartDate && customEndDate) {
      setDateRange(buildDateRangeISO(customStartDate, customEndDate));
    }
  };

  const fetchGuestInfo = async () => {
    try {
      // Fetch all guest types using the new API endpoints with POST
      const types = ['InHouse', 'TodaysArrival', 'TodaysDeparture', 'Next4Days'];
      let allGuestsData = [];
      
      for (const type of types) {
        try {
          const res = await authaltFetch('/guestlistdatav3', {
            type,
            index: 'atmantan'
          });
          if (res.ok) {
            const response = await res.json();
            const categoryKey = type === 'InHouse' ? 'inHouse' :
                               type === 'TodaysArrival' ? 'todaysArrival' :
                               type === 'TodaysDeparture' ? 'todaysDeparture' : 'arrivalNext4Days';
            const guestsFromType = response[categoryKey]?.data || [];
            allGuestsData = allGuestsData.concat(guestsFromType);
          }
        } catch (e) {
          console.error(`Failed to fetch ${type}:`, e);
        }
      }
      
      const guest = allGuestsData.find(g => 
        g.guestId === guestId || 
        g.id === guestId ||
        (g.firstName && g.lastName && `${g.firstName} ${g.lastName}` === guestName) ||
        g.guestName === guestName
      );
      
      if (guest) {
        const resolvedGuestId = guest.guestId || guest.id || guestId;
        const resolvedBookingId = bookingIdProp
          || guest.bookingId
          || guest.booking_id
          || guest.BookingId
          || '';

        setGuestInfo({
          guestId: resolvedGuestId,
          bookingId: resolvedBookingId,
          guestName: guest.guestName || guest.guest_name || `${guest.firstName || ''} ${guest.lastName || ''}`.trim() || guestName,
          room: guest.room || guest.roomNo || guest.location || '',
          consultingDoctor: guest.consultingDoctor || guest.doctor || '',
          retreatName: guest.package || guest.program || 'Wellness Retreat',
          checkIn: toISO(getFirstNonEmpty(guest, ['checkIn', 'checkin', 'check_in', 'startDate', 'start', 'arrivalDate', 'ArrivalDate'])),
          checkOut: toISO(getFirstNonEmpty(guest, ['checkOut', 'checkout', 'check_out', 'endDate', 'end', 'departureDate', 'DepartureDate']))
        });
      } else {
        setGuestInfo({
          guestId: guestId || '',
          bookingId: bookingIdProp || '',
          guestName: guestName || '',
          room: '',
          consultingDoctor: '',
          retreatName: 'Wellness Retreat',
          checkIn: '',
          checkOut: ''
        });
      }
    } catch (error) {
      console.error('Error fetching guest info:', error);
      setGuestInfo({
        guestId: guestId || '',
        bookingId: bookingIdProp || '',
        guestName: guestName || '',
        room: '',
        consultingDoctor: '',
        retreatName: 'Wellness Retreat',
        checkIn: '',
        checkOut: ''
      });
    }
  };

  const fetchAllActivities = async () => {
    if (dateRange.length === 0) return;
    
    try {
      const startDate = dateRange[0];
      const endDate = dateRange[dateRange.length - 1];
      console.log('Fetching activities from:', startDate, 'to:', endDate);
      const response = await apiGet(`/api/activities/timetable?start_date=${startDate}&end_date=${endDate}`);
      if (response.ok) {
        const data = await response.json();
        console.log('Activities fetched:', data.length, 'activities');
        setAllActivities(Array.isArray(data) ? data : []);
      } else {
        console.error('Failed to fetch activities:', response.status);
        setAllActivities([]);
      }
    } catch (error) {
      console.error('Error fetching all activities:', error);
      setAllActivities([]);
    }
  };

  const fetchItinerary = async () => {
    if (!guestId && !guestName) return;
    
    try {
      setLoading(true);
      const allBookings = [];
      
      for (const date of dateRange) {
        const response = await apiGet(`/api/bookings/${date}`);
        if (response.ok) {
          const data = await response.json();
          const guestBookings = (Array.isArray(data) ? data : []).filter(b => {
            if (guestId) {
              return b.guestId === guestId || b.guest_id === guestId;
            } else if (guestName) {
              return (b.guestName || '').toLowerCase().includes(guestName.toLowerCase());
            }
            return false;
          });
          
          guestBookings.forEach(b => {
            b.date = date;
            // Map room name to room location
            if (b.room && roomsData[b.room]) {
              b.location = roomsData[b.room].location || '';
            }
          });
          
          allBookings.push(...guestBookings);
        }
      }
      
      allBookings.sort((a, b) => {
        if (a.date !== b.date) {
          return a.date.localeCompare(b.date);
        }
        return (a.startTime || '').localeCompare(b.startTime || '');
      });
      
      setBookings(allBookings);
    } catch (error) {
      console.error('Error fetching itinerary:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  const formatDateForPDF = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-GB', { month: 'short' }).toUpperCase();
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    return timeStr.substring(0, 5);
  };

  const formatTimeRange = (startTime, endTime) => {
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const calculateNights = (checkInStr, checkOutStr) => {
    if (!checkInStr || !checkOutStr) return 0;
    const checkIn = new Date(checkInStr);
    const checkOut = new Date(checkOutStr);
    const diffTime = Math.abs(checkOut - checkIn);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Format date header for activities table (e.g., "Sat 18 Oct")
  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  // Get unique dates from activities
  const getActivityDates = () => {
    const dates = new Set();
    allActivities.forEach(activity => {
      if (activity.date) dates.add(activity.date);
    });
    return Array.from(dates).sort();
  };

  // Get unique time ranges from all activities (for grid view - actual activity times)
  const getUniqueTimeRanges = () => {
    const timeRanges = new Set();
    allActivities.forEach(activity => {
      if (activity.start_time && activity.end_time) {
        const timeRange = `${activity.start_time.substring(0, 5)}-${activity.end_time.substring(0, 5)}`;
        timeRanges.add(timeRange);
      }
    });
    return Array.from(timeRanges).sort();
  };

  // Generate fixed 1-hour time slots (for timeslot view)
  // Only returns slots that have activities starting in them
  const getFixedHourlySlots = () => {
    const slots = [];
    const activityDates = getActivityDates();
    
    // Generate 1-hour slots from 6 AM to 9 PM
    for (let hour = 6; hour <= 21; hour++) {
      const startTime = `${String(hour).padStart(2, '0')}:00`;
      const endTime = `${String(hour + 1).padStart(2, '0')}:00`;
      const slotRange = `${startTime}-${endTime}`;
      
      // Check if this slot has any activities starting in it across all dates
      const hasActivities = activityDates.some(date => {
        const activities = getActivitiesStartingInSlot(date, startTime, endTime);
        return activities.length > 0;
      });
      
      if (hasActivities) {
        slots.push(slotRange);
      }
    }
    return slots;
  };

  // Format time for display (convert 24h to 12h with AM/PM)
  const formatTimeForTable = (time24) => {
    if (!time24) return '';
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // Get activities for a specific date and exact time range (timeslot view)
  const getActivitiesForTimeRange = (date, timeRange) => {
    const [startTime, endTime] = timeRange.split('-');
    return allActivities.filter(activity => {
      if (activity.date !== date) return false;
      const activityStart = activity.start_time?.substring(0, 5);
      const activityEnd = activity.end_time?.substring(0, 5);
      return activityStart === startTime && activityEnd === endTime;
    });
  };

  // Get activities that START within a time slot (timeslot view)
  const getActivitiesStartingInSlot = (date, slotStart, slotEnd) => {
    return allActivities.filter(activity => {
      if (activity.date !== date) return false;
      const activityStart = activity.start_time?.substring(0, 5);
      // Activity must start within this slot
      return activityStart >= slotStart && activityStart < slotEnd;
    });
  };

  // Get max activity count for any timeslot (for dynamic row height in timeslot view)
  const getMaxActivitiesPerTimeslot = (timeRanges, activityDates) => {
    let maxCount = 1;
    timeRanges.forEach(timeRange => {
      activityDates.forEach(date => {
        const activities = getActivitiesForTimeRange(date, timeRange);
        maxCount = Math.max(maxCount, activities.length);
      });
    });
    return maxCount;
  };

  const getActivityIcon = (treatmentName) => {
    const name = (treatmentName || '').toLowerCase();
    const iconStyle = { fontSize: 20, color: ATMANTAN_GOLD };
    
    if (isDoctorConsultation(treatmentName)) {
      return <MedicalServicesIcon sx={iconStyle} />;
    }
    if (name.includes('breakfast') || name.includes('wake up')) {
      return <FreeBreakfastIcon sx={iconStyle} />;
    } else if (name.includes('lunch') || name.includes('dinner') || name.includes('mid morning') || name.includes('evening') || name.includes('mid-day') || name.includes('mid evening')) {
      return <RestaurantIcon sx={iconStyle} />;
    } else if (name.includes('fitness') || name.includes('gym') || name.includes('training')) {
      return <FitnessCenterIcon sx={iconStyle} />;
    } else if (name.includes('yoga') || name.includes('meditation') || name.includes('kriya')) {
      return <SelfImprovementIcon sx={iconStyle} />;
    } else {
      return <SpaIcon sx={iconStyle} />;
    }
  };

  // Get icon file path based on treatment name
  const getIconPath = (treatmentName) => {
    const name = (treatmentName || '').toLowerCase();
    
    if (isDoctorConsultation(treatmentName)) {
      return '/stethoscope.svg';
    }
    if (name.includes('breakfast') || name.includes('wake up')) {
      return '/breakfast.png';
    } else if (name.includes('lunch') || name.includes('dinner') || name.includes('mid morning') || name.includes('evening') || name.includes('mid-day') || name.includes('mid evening')) {
      return '/lunch-dinner.png';
    } else if (name.includes('kriya') || name.includes('yoga') || name.includes('meditation')) {
      return '/kriya.png';
    } else {
      return '/therapy.png'; // Default for spa/therapy/fitness activities
    }
  };

  // Get icon for HTML print
  const getActivityIconSVG = (treatmentName) => {
    const iconPath = getIconPath(treatmentName);
    const src = iconPath.startsWith('http') ? iconPath : `${window.location.origin}${iconPath}`;
    return `<img src="${src}" alt="icon" width="20" height="20" style="display: block;" />`;
  };

  const getPrintAssetUrl = (path) => (
    path.startsWith('http') ? path : `${window.location.origin}${path}`
  );

  const getPrintFaviconTag = () => (
    `<link rel="icon" type="image/png" href="${getPrintAssetUrl('/atmantan-logo-a.png')}" />`
  );

  const getDoctorRecommendationsForDay = (dayNum) => (
    getUniqueGuestTaskLabels(filterGuestTasksForDay(guestTasks, dayNum))
  );

  const renderBookingCardHTML = (booking) => {
    const location = (booking.location || '').trim();
    return `
      <div class="booking-card">
        <div class="icon-wrapper">
          ${getActivityIconSVG(booking.treatment)}
        </div>
        <div class="booking-main">
          <div class="booking-content">
            <div class="time">${formatTimeRange(booking.startTime, booking.endTime)}</div>
            <div class="treatment">${formatDisplayTreatmentName(booking.treatment)}</div>
            ${!booking.isStandard ? '<div class="orientation">Orientation: 10 mins before</div>' : ''}
          </div>
          ${location ? `<div class="booking-location">${location}</div>` : ''}
        </div>
      </div>
    `;
  };

  const estimateBookingCardHeightPDF = (doc, booking, colWidth) => {
    const treatmentWidth = colWidth * 0.52;
    const locWidth = colWidth * 0.35;
    let h = 4;
    const treatmentLines = doc.splitTextToSize(
      formatDisplayTreatmentName(booking.treatment),
      treatmentWidth
    );
    h += treatmentLines.length * 4;
    if (!booking.isStandard) h += 4;
    const location = (booking.location || '').trim();
    if (location) {
      const locLines = doc.splitTextToSize(location, locWidth);
      h = Math.max(h, 4 + locLines.length * 3.5);
    }
    return h + 5;
  };

  const drawBookingCardInPDF = (doc, booking, x, y, colWidth) => {
    const iconOffset = 8;
    const contentX = x + iconOffset;
    const locWidth = colWidth * 0.35;
    const treatmentWidth = colWidth - iconOffset - locWidth - 2;
    const locX = x + colWidth - locWidth;

    drawActivityIconInPDF(doc, booking.treatment, x, y - 1);

    doc.setFontSize(9);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(formatTimeRange(booking.startTime, booking.endTime), contentX, y);

    const location = (booking.location || '').trim();
    if (location) {
      doc.setFontSize(8);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(80, 80, 80);
      const locLines = doc.splitTextToSize(location, locWidth);
      doc.text(locLines, locX, y);
    }

    let cy = y + 4;
    doc.setFontSize(10);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    const treatmentLines = doc.splitTextToSize(
      formatDisplayTreatmentName(booking.treatment),
      treatmentWidth
    );
    doc.text(treatmentLines, contentX, cy);
    cy += treatmentLines.length * 4;

    if (!booking.isStandard) {
      doc.setFontSize(8);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(100, 100, 100);
      doc.text('Orientation: 10 mins before', contentX, cy);
      cy += 4;
    }

    const cardBottom = cy + 2;
    doc.setDrawColor(199, 174, 110);
    doc.setLineWidth(0.3);
    doc.line(x, cardBottom, x + colWidth, cardBottom);

    return cardBottom - y + 3;
  };

  const drawDayBookingsTwoColumnPDF = (doc, dayBookings, margin, pageWidth, pageHeight, startY) => {
    const columnGap = 8;
    const colWidth = (pageWidth - margin * 2 - columnGap) / 2;
    const leftX = margin;
    const rightX = margin + colWidth + columnGap;
    const pageBottom = pageHeight - 40;
    let leftY = startY;
    let rightY = startY;

    dayBookings.forEach((booking) => {
      const itemHeight = estimateBookingCardHeightPDF(doc, booking, colWidth);

      const placeOnColumn = (x, y) => drawBookingCardInPDF(doc, booking, x, y, colWidth);

      if (leftY + itemHeight <= pageBottom) {
        leftY += placeOnColumn(leftX, leftY);
      } else if (rightY + itemHeight <= pageBottom) {
        rightY += placeOnColumn(rightX, rightY);
      } else {
        doc.addPage();
        leftY = margin;
        rightY = margin;
        leftY += placeOnColumn(leftX, leftY);
      }
    });

    return Math.max(leftY, rightY);
  };

  const drawDoctorRecommendationsPDF = (doc, dayNum, margin, pageWidth, startY, pageHeight) => {
    const dayTasks = getDoctorRecommendationsForDay(dayNum);
    let yPos = startY + 4;

    if (yPos > pageHeight - 30) {
      doc.addPage();
      yPos = margin;
    }

    doc.setFontSize(10);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(176, 152, 88);
    doc.text('Your doctor recommends the following for you:', margin, yPos);
    yPos += 6;

    doc.setFontSize(8);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(0, 0, 0);
    const maxWidth = pageWidth - margin * 2;

    if (dayTasks.length > 0) {
      dayTasks.forEach((recommendation) => {
        if (yPos > pageHeight - 20) {
          doc.addPage();
          yPos = margin;
        }
        doc.text('☐', margin + 2, yPos);
        const lines = doc.splitTextToSize(recommendation, maxWidth - 8);
        doc.text(lines, margin + 8, yPos);
        yPos += lines.length * 4.5;
      });
    } else {
      doc.setFont('Roboto', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text(
        !guestInfo?.bookingId ? '(Booking ID required to load AIIMS tasks)' : '(No tasks assigned by doctor)',
        margin,
        yPos
      );
      yPos += 5;
      doc.setTextColor(0, 0, 0);
    }

    return yPos + 4;
  };

  const drawActivityIconInPDF = (doc, treatmentName, x, y) => {
    try {
      const iconPath = getIconPath(treatmentName);
      const iconImg = new Image();
      iconImg.src = iconPath;
      const format = iconPath.endsWith('.svg') ? 'SVG' : 'PNG';
      
      // Add icon as image (width: 5mm, height will auto-scale)
      doc.addImage(iconImg, format, x, y, 5, 5);
    } catch (error) {
      console.error('Error adding icon to PDF:', error);
      // Fallback: draw a simple circle
      doc.setDrawColor(199, 174, 110);
      doc.setFillColor(199, 174, 110);
      doc.circle(x + 2.5, y + 2.5, 1.5, 'F');
    }
  };

  // Group bookings by date and add standard daily items (meals only - activities come from actual enrollments)
  const bookingsByDate = useMemo(() => {
    const grouped = {};
    
    bookings.forEach(booking => {
      const date = booking.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push({
        ...booking,
        treatment: formatDisplayTreatmentName(booking.treatment)
      });
    });
    
    // Add standard items to each day and sort by time
    Object.keys(grouped).forEach(date => {
      STANDARD_DAILY_MEALS.forEach(item => {
        const alreadyHasMeal = grouped[date].some((booking) => {
          const existing = (booking.treatment || '').toLowerCase();
          const standard = item.treatment.toLowerCase();
          return existing === standard || existing.includes(standard);
        });
        if (!alreadyHasMeal) {
          grouped[date].push({ ...item, date });
        }
      });
      
      // Sort by start time (convert to minutes for proper chronological order)
      grouped[date].sort((a, b) => {
        const timeA = a.startTime || '99:99';
        const timeB = b.startTime || '99:99';
        
        // Convert HH:MM to minutes for accurate comparison
        const getMinutes = (time) => {
          const [hours, mins] = time.split(':').map(Number);
          return hours * 60 + mins;
        };
        
        return getMinutes(timeA) - getMinutes(timeB);
      });
    });
    
    return grouped;
  }, [bookings]);

  const getDayTreatmentDescriptions = (dayBookings) => {
    const seen = new Set();
    const result = [];

    dayBookings.forEach((booking) => {
      if (booking.isStandard || isStandardMealName(booking.treatment)) return;
      const key = (booking.treatment || '').toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      const description = treatmentDescriptions[key];
      if (description) {
        result.push({
          name: formatDisplayTreatmentName(booking.treatment),
          description
        });
      }
    });

    return result;
  };

  const renderDoctorRecommendationsText = (dayNum = null) => {
    const recommendations = dayNum != null
      ? getDoctorRecommendationsForDay(dayNum)
      : doctorRecommendations;

    if (recommendations.length > 0) {
      return recommendations.map((item, index) => (
        <Typography
          key={index}
          component="li"
          variant="body2"
          sx={{ mb: 0.5, fontSize: '0.875rem', display: 'flex', alignItems: 'flex-start', gap: 1 }}
        >
          <Box component="span" sx={{ lineHeight: 1.4 }}>☐</Box>
          <Box component="span">{item}</Box>
        </Typography>
      ));
    }

    const placeholder = !guestInfo?.bookingId
      ? '(Booking ID required to load AIIMS tasks)'
      : '(No tasks assigned by doctor)';

    return (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        {placeholder}
      </Typography>
    );
  };

  const renderPrintDoctorRecommendationsHTML = (dayNum) => {
    const dayTasks = getDoctorRecommendationsForDay(dayNum);

    if (dayTasks.length > 0) {
      return `
        <div class="doctor-recommendations">
          <h3 class="doctor-recommendations-title">Your doctor recommends the following for you:</h3>
          <ul class="doctor-recommendations-list">
            ${dayTasks.map((item) => `<li><span class="task-checkbox">☐</span> ${item}</li>`).join('')}
          </ul>
        </div>
      `;
    }

    const placeholder = !guestInfo?.bookingId
      ? '(Booking ID required to load AIIMS tasks)'
      : '(No tasks assigned by doctor)';

    return `
      <div class="doctor-recommendations">
        <h3 class="doctor-recommendations-title">Your doctor recommends the following for you:</h3>
        <p class="footer-placeholder">${placeholder}</p>
      </div>
    `;
  };

  const renderPrintTreatmentDescriptionsHTML = (dayBookings) => {
    const items = getDayTreatmentDescriptions(dayBookings);
    if (items.length === 0) return '';

    return `
      <div class="treatment-descriptions">
        <h3 class="treatment-descriptions-title">Treatments &amp; Activities</h3>
        <div class="treatment-descriptions-grid">
          ${items.map((item) => `
            <div class="treatment-description-item">
              <div class="treatment-description-name">${item.name}</div>
              <div class="treatment-description-text">${item.description}</div>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  };

  // Calculate day numbers relative to check-in
  const getDayNumber = (dateStr) => {
    if (!guestInfo?.checkIn) return null;
    const checkIn = new Date(guestInfo.checkIn);
    const current = new Date(dateStr);
    const diffTime = current - checkIn;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays + 1;
  };

  // Generate PDF in Card format (detailed view with sections)
  const generateCardPDF = async (dayFilter = 'all') => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = margin;

    // Add Roboto fonts
    await addRobotoFonts(doc);

    // Filter bookings by day
    const datesToInclude = dayFilter === 'all' 
      ? Object.keys(bookingsByDate) 
      : Object.keys(bookingsByDate).filter(date => getDayNumber(date) === parseInt(dayFilter));

    // Load logo
    const logoImg = new Image();
    logoImg.src = '/Atmantan.png';

    datesToInclude.forEach((date, pageIndex) => {
      const dayBookings = bookingsByDate[date];
      const dayNum = getDayNumber(date);

      if (pageIndex > 0) {
        doc.addPage();
        yPos = margin;
      }

      // Add logo on each page (right side)
      try {
        const logoWidth = 35;
        doc.addImage(logoImg, 'PNG', pageWidth - margin - logoWidth, yPos, logoWidth, 0);
      } catch (e) {
        // Fallback to text if image fails
        doc.setFontSize(12);
        doc.setTextColor(199, 174, 110);
      doc.setFont('Roboto', 'normal');
        const logoText = 'ātmantan';
        const logoWidth = doc.getTextWidth(logoText);
        doc.text(logoText, pageWidth - margin - logoWidth, yPos + 5);
      }
      
      // Guest Name (left side)
      doc.setFontSize(18);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(0, 0, 0);
      doc.text(guestInfo?.guestName || 'Guest', margin, yPos);
      yPos += 7;

      // Room number
        doc.setFontSize(10);
        doc.setFont('Roboto', 'normal');
      doc.text(`Room no: ${guestInfo?.room || 'N/A'}`, margin, yPos);
        yPos += 5;

      // Guest UID
      doc.text(`Guest UID: ${guestInfo?.guestId || 'N/A'}`, margin, yPos);
      yPos += 4;

      // Retreat
      doc.setFontSize(9);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(0, 0, 0);
      const retreatText = `Retreat: ${guestInfo?.retreatName || 'N/A'}${guestInfo?.checkIn && guestInfo?.checkOut ? ` (${calculateNights(guestInfo.checkIn, guestInfo.checkOut)} Nights)` : ''}`;
      doc.text(retreatText, margin, yPos);
      yPos += 2;
      
      // Day number and Date (right side, aligned below logo)
      let rightYPos = margin + 18;
      if (dayNum) {
        doc.setFontSize(16);
        doc.setFont('Roboto', 'bold');
        doc.setTextColor(0, 0, 0);
        const dayText = `DAY ${dayNum}`;
        const dayWidth = doc.getTextWidth(dayText);
        doc.text(dayText, pageWidth - margin - dayWidth, rightYPos);
        rightYPos += 5;
      }

      doc.setFontSize(10);
      doc.setFont('Roboto', 'normal');
      doc.setTextColor(0, 0, 0);
      const dateText = `Date: ${formatDate(date)}`;
      const dateWidth = doc.getTextWidth(dateText);
      doc.text(dateText, pageWidth - margin - dateWidth, rightYPos);
      rightYPos += 5;

      doc.setFontSize(9);
      const doctorLabel = 'Consulting Doctor:';
      const doctorValue = guestInfo?.consultingDoctor || 'N/A';
      const doctorText = `${doctorLabel} ${doctorValue}`;
      const doctorWidth = doc.getTextWidth(doctorText);
      doc.text(doctorText, pageWidth - margin - doctorWidth, rightYPos);
      
      doc.setTextColor(0, 0, 0);
      yPos = Math.max(yPos, rightYPos + 4);

      // Divider
      doc.setDrawColor(ATMANTAN_DARK_GOLD);
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 5;

      // Bookings: two columns on same page (left fills first, then right)
      yPos = drawDayBookingsTwoColumnPDF(doc, dayBookings, margin, pageWidth, pageHeight, yPos);
      yPos += 4;

      const dayDescriptions = getDayTreatmentDescriptions(dayBookings);
      if (dayDescriptions.length > 0) {
        if (yPos > pageHeight - 50) {
          doc.addPage();
          yPos = margin;
        }

        yPos += 4;
        doc.setFontSize(10);
        doc.setFont('Roboto', 'bold');
        doc.setTextColor(176, 152, 88);
        doc.text('Treatments & Activities', margin, yPos);
        yPos += 6;

        const columnWidth = (pageWidth - (margin * 2) - 8) / 2;
        dayDescriptions.forEach((item, index) => {
          const column = index % 2;
          const row = Math.floor(index / 2);
          const x = margin + column * (columnWidth + 8);
          const baseY = yPos + row * 18;

          if (baseY > pageHeight - 25) {
            doc.addPage();
            yPos = margin;
          }

          doc.setFontSize(9);
          doc.setFont('Roboto', 'bold');
          doc.setTextColor(0, 0, 0);
          doc.text(item.name, x, baseY);

          doc.setFontSize(8);
          doc.setFont('Roboto', 'normal');
          doc.setTextColor(80, 80, 80);
          const descLines = doc.splitTextToSize(item.description, columnWidth);
          doc.text(descLines, x, baseY + 4);
        });

        const rows = Math.ceil(dayDescriptions.length / 2);
        yPos += rows * 18 + 4;
      }

      yPos = drawDoctorRecommendationsPDF(doc, dayNum, margin, pageWidth, yPos, pageHeight);
    });

    // Add new page for All Available Activities
    doc.addPage();
    yPos = margin;

    // Activities Table Title
    doc.setFontSize(14);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(176, 152, 88);
    doc.text('All Available Activities', margin, yPos);
    yPos += 8;

    // Get activity dates and time ranges based on layout
    const activityDates = getActivityDates();
    const timeRanges = activityLayout === 'grid' ? getUniqueTimeRanges() : getFixedHourlySlots();
    const getActivitiesFunc = activityLayout === 'grid' ? getActivitiesForTimeRange : null; // For timeslot view, we'll use getActivitiesStartingInSlot
    
    console.log('PDF Generation - Activities count:', allActivities.length);
    console.log('PDF Generation - Activity dates:', activityDates);
    console.log('PDF Generation - Time ranges:', timeRanges);
    console.log('PDF Generation - Layout:', activityLayout);

    if (activityDates.length > 0 && timeRanges.length > 0) {
      // Table header
      doc.setFontSize(7);
      doc.setFont('Roboto', 'bold');
      doc.setTextColor(0, 0, 0);
      
      const timeColWidth = activityLayout === 'grid' ? 35 : 30;
      const dateColWidth = (pageWidth - 2 * margin - timeColWidth) / activityDates.length;
      
      // Draw header row
      doc.text('FROM TIME - TO TIME', margin + 2, yPos);
      activityDates.forEach((date, index) => {
        const x = margin + timeColWidth + index * dateColWidth;
        const headerText = formatDateHeader(date);
        doc.text(headerText, x + 2, yPos);
      });
        yPos += 3;
      
      // Header divider
      doc.setDrawColor(0, 0, 0);
      doc.setLineWidth(0.3);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 2;
      
      // Table rows
      doc.setFont('Roboto', 'normal');
      doc.setFontSize(5);
      
      timeRanges.forEach((timeRange) => {
        const [startTime, endTime] = timeRange.split('-');
        const timeText = `${formatTimeForTable(startTime)} - ${formatTimeForTable(endTime)}`;
        
        // Check if we need a new page
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = margin;
          
          // Repeat header on new page
          doc.setFont('Roboto', 'bold');
          doc.setFontSize(7);
          doc.text('FROM TIME - TO TIME', margin + 2, yPos);
          activityDates.forEach((date, index) => {
            const x = margin + timeColWidth + index * dateColWidth;
            doc.text(formatDateHeader(date), x + 2, yPos);
          });
          yPos += 3;
          doc.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 2;
          doc.setFont('Roboto', 'normal');
          doc.setFontSize(5);
        }
        
        let maxRowHeight = 8;
        
        // Time column
        doc.setFontSize(6);
        doc.setFont('Roboto', 'bold');
        doc.text(timeText, margin + 1, yPos + 3);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(5);
        
        // Activity columns
        activityDates.forEach((date, index) => {
          const x = margin + timeColWidth + index * dateColWidth;
          const activities = activityLayout === 'grid' 
            ? getActivitiesFunc(date, timeRange)
            : getActivitiesStartingInSlot(date, timeRange.split('-')[0], timeRange.split('-')[1]);
          
          if (activities.length > 0) {
            let cellY = yPos + 3;
            activities.forEach((activity, actIndex) => {
              if (actIndex > 0) {
                cellY += 2;
              }
              
              // Activity name with endurance rating (bold)
              doc.setFont('Roboto', 'bold');
              let activityNameWithStars = activity.activity_name;
              if (activity.endurance_rating) {
                const stars = '\u2605'.repeat(activity.endurance_rating);
                activityNameWithStars = `${stars} ${activity.activity_name}`;
              }
              doc.text(activityNameWithStars, x + 2, cellY, { maxWidth: dateColWidth - 4 });
              cellY += 2.5;
              
              // Show actual time for all views
              doc.setFont('Roboto', 'normal');
              doc.setTextColor(80, 80, 80);
              const timeText = `${activity.start_time?.substring(0, 5)} - ${activity.end_time?.substring(0, 5)}`;
              doc.text(timeText, x + 2, cellY, { maxWidth: dateColWidth - 4 });
              cellY += 2.5;
              
              // Location
              doc.setFont('Roboto', 'normal');
              doc.setTextColor(100, 100, 100);
              const locationText = `@ ${activity.room_name || ''}`;
              doc.text(locationText, x + 2, cellY, { maxWidth: dateColWidth - 4 });
              cellY += 2.5;

        doc.setTextColor(0, 0, 0);
            });
            const cellHeight = cellY - (yPos + 3) + 2;
            maxRowHeight = Math.max(maxRowHeight, cellHeight);
          }
        });
        
        yPos += maxRowHeight;
        
        // Draw row divider
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.1);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 1;
      });
      
        yPos += 5;
    } else {
      doc.setFontSize(8);
      doc.setFont('Roboto', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('No activities available for the selected date range.', margin, yPos);
      yPos += 10;
    }

    // Add information note below activities
    yPos += 8;
    
    // Check if we need a new page for the notes
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = margin;
    }
    
    doc.setFontSize(9);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Important Information:', margin, yPos);
    yPos += 6;
    
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(8);
    const lineSpacingCard = 4.5;
    const bulletOffsetCard = 3;
    
    // Note 1
    doc.text('\u2022', margin, yPos);
    const note1CardPDF = 'Choose Classes That Suit Your Fitness Level (\u2605BEGINNER \u2014\u2605\u2605 INTERMEDIATE \u2014\u2605\u2605\u2605ADVANCE) All Classes Have Been Marked With Stars As Per Their Competence Level.';
    const note1CardLines = doc.splitTextToSize(note1CardPDF, pageWidth - 2 * margin - bulletOffsetCard);
    doc.text(note1CardLines, margin + bulletOffsetCard, yPos);
    yPos += note1CardLines.length * lineSpacingCard + 3;
    
    // Note 2
    doc.text('\u2022', margin, yPos);
    const note2CardPDF = 'Report (5min Early) To All The Group Activities, So That You Can Setup Mats, follow INITIAL-IMPORTANT-INSTRUCTIONS OF The Session, Warmup / Loosening Practices, etc & Get The Completeness Of The Group Activity.';
    const note2CardLines = doc.splitTextToSize(note2CardPDF, pageWidth - 2 * margin - bulletOffsetCard);
    doc.text(note2CardLines, margin + bulletOffsetCard, yPos);
    yPos += note2CardLines.length * lineSpacingCard + 3;
    
    // Note 3
    doc.text('\u2022', margin, yPos);
    const note3CardPDF = 'Comfortable Fitness Gear Are Mandatory For All Fitness Activities \u2014 Indoor Sports Shoes, Track Pants/Sports Trouser, Sports T-Shirt.';
    const note3CardLines = doc.splitTextToSize(note3CardPDF, pageWidth - 2 * margin - bulletOffsetCard);
    doc.text(note3CardLines, margin + bulletOffsetCard, yPos);
    yPos += note3CardLines.length * lineSpacingCard + 3;
    
    // Note 4
    doc.text('\u2022', margin, yPos);
    const note4CardPDF = 'Personal Kriya Kits Are Available For Purchase @Wellness.';
    const note4CardLines = doc.splitTextToSize(note4CardPDF, pageWidth - 2 * margin - bulletOffsetCard);
    doc.text(note4CardLines, margin + bulletOffsetCard, yPos);
    yPos += note4CardLines.length * lineSpacingCard + 8;

    // Add new page for Footer with General Instructions, Doctor Recommendations, and Wellness Guidelines
    doc.addPage();
    yPos = margin;

    // Footer Title
    doc.setFontSize(14);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(176, 152, 88);
    doc.text('Important Information', margin, yPos);
    yPos += 8;

    // General Instructions
    doc.setFontSize(10);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('General Instructions:', margin, yPos);
    yPos += 6;

    doc.setFontSize(8);
    doc.setFont('Roboto', 'normal');
    const maxWidth = pageWidth - (margin * 2);
    GENERAL_INSTRUCTIONS.forEach((instruction) => {
      doc.text('•', margin + 2, yPos);
      const lines = doc.splitTextToSize(instruction, maxWidth - 5);
      doc.text(lines, margin + 5, yPos);
      yPos += lines.length * 4.5;
    });
    yPos += 4;

    doc.setFontSize(8);
    doc.setFont('Roboto', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text('Doctor recommendations are listed on each day\'s itinerary page.', margin, yPos);
    yPos += 8;
    doc.setTextColor(0, 0, 0);

    // Wellness Guidelines Section
    doc.setFontSize(10);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Wellness Guidelines:', margin, yPos);
    yPos += 6;

    doc.setFontSize(8);
    doc.setFont('Roboto', 'normal');
    WELLNESS_GUIDELINES.forEach((guideline) => {
      doc.text('•', margin + 2, yPos);
      const lines = doc.splitTextToSize(guideline, maxWidth - 5);
      doc.text(lines, margin + 5, yPos);
      yPos += lines.length * 4.5;
    });

    // Page footer
      doc.setFontSize(7);
    doc.setFont('Roboto', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text('Generated by Atmantan Wellness Scheduler', pageWidth / 2, pageHeight - 8, { align: 'center' });

    // Save PDF
    const filename = dayFilter === 'all' 
      ? `${guestInfo?.guestName || 'Guest'}_Full_Itinerary_Card.pdf`
      : `${guestInfo?.guestName || 'Guest'}_Day${dayFilter}_Itinerary_Card.pdf`;
    doc.save(filename);
  };

  // Generate PDF in List/Table format (matches UI list view exactly)
  const generateListPDF = async (dayFilter = 'all') => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    let yPos = margin;

    // Add Roboto fonts
    await addRobotoFonts(doc);

    // Filter bookings by day
    const datesToInclude = dayFilter === 'all' 
      ? Object.keys(bookingsByDate) 
      : Object.keys(bookingsByDate).filter(date => getDayNumber(date) === parseInt(dayFilter));

    // Load logo
    const logoImg = new Image();
    logoImg.src = '/Atmantan.png';

    // Add logo image (right side) - auto-maintain aspect ratio with 35mm width
    try {
      const logoWidth = 35;
      doc.addImage(logoImg, 'PNG', pageWidth - margin - logoWidth, margin - 2, logoWidth, 0);
    } catch (e) {
      // Fallback to text if image fails
      doc.setFontSize(12);
      doc.setTextColor(199, 174, 110);
      doc.setFont('Roboto', 'normal');
      const logoText = 'ātmantan';
      const logoWidth = doc.getTextWidth(logoText);
      doc.text(logoText, pageWidth - margin - logoWidth, margin);
    }

    // Header Title
    doc.setFontSize(16);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(176, 152, 88); // ATMANTAN_DARK_GOLD
    doc.text('Guest Itinerary', margin, yPos);
    yPos += 8;

    // Guest Information Grid (2 columns)
    doc.setFontSize(8);
    const col1X = margin;
    const col2X = margin + 90;
    const lineHeight = 5;

    // Row 1: Guest Name | Room No
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Guest Name:', col1X, yPos);
    doc.text('Room No:', col2X, yPos);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(guestInfo?.guestName || 'N/A', col1X + 22, yPos);
    doc.text(guestInfo?.room || 'N/A', col2X + 18, yPos);
    yPos += lineHeight;

    // Row 1b: Guest ID
    doc.setFont('Roboto', 'bold');
    doc.text('Guest ID:', col1X, yPos);
    doc.setFont('Roboto', 'normal');
    doc.text(guestInfo?.guestId || 'N/A', col1X + 22, yPos);
    yPos += lineHeight;

    // Row 2: Check-in Date | Check-out Date
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Check-in Date:', col1X, yPos);
    doc.text('Check-out Date:', col2X, yPos);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(0, 0, 0);
    doc.text(guestInfo?.checkIn ? formatDate(guestInfo.checkIn) : 'N/A', col1X + 22, yPos);
    doc.text(guestInfo?.checkOut ? formatDate(guestInfo.checkOut) : 'N/A', col2X + 23, yPos);
    yPos += lineHeight;

    // Row 3: Retreat Name | Consulting Doctor
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(100, 100, 100);
    doc.text('Retreat Name:', col1X, yPos);
    doc.text('Consulting Doctor:', col2X, yPos);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(0, 0, 0);
    const packageName = guestInfo?.retreatName || 'N/A';
    const nights = guestInfo?.checkIn && guestInfo?.checkOut ? ` (${calculateNights(guestInfo.checkIn, guestInfo.checkOut)} Nights)` : '';
    doc.text(packageName + nights, col1X + 22, yPos);
    doc.text(guestInfo?.consultingDoctor || 'N/A', col2X + 30, yPos);
    yPos += lineHeight + 3;

    // Main divider
    doc.setDrawColor(176, 152, 88); // ATMANTAN_DARK_GOLD
    doc.setLineWidth(0.5);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 6;

    // Table column positions - adjusted for new date format and spacing
    const colX1 = margin;              // Day/Date column (merged) - wider for "10 JAN 2025"
    const colX2 = margin + 45;         // Time column - more spacing before
    const colX3 = margin + 72;         // Treatment column
    const colX4 = pageWidth - margin - 40; // Location column

    // Column headers - consistent font size
    doc.setFontSize(8);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Day / Date', colX1, yPos);
    doc.text('Time', colX2, yPos);
    doc.text('Treatment / Orientation', colX3, yPos);
    doc.text('Location', colX4, yPos);
    doc.setTextColor(0, 0, 0);
    yPos += 2;

    // Light divider
        doc.setDrawColor(ATMANTAN_GOLD);
    doc.setLineWidth(0.2);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 5;

    // Group bookings by date (like UI)
    let lastDate = null;
    datesToInclude.forEach((date) => {
      const dayBookings = bookingsByDate[date];
      const dayNum = getDayNumber(date);

      dayBookings.forEach((booking, bookingIndex) => {
        // Check if we need a new page
        if (yPos > pageHeight - 25) {
          doc.addPage();
          yPos = margin;
          
          // Repeat column headers
          doc.setFontSize(8);
          doc.setFont('Roboto', 'bold');
          doc.setTextColor(100, 100, 100);
          doc.text('Day / Date', colX1, yPos);
          doc.text('Time', colX2, yPos);
          doc.text('Treatment / Orientation', colX3, yPos);
          doc.text('Location', colX4, yPos);
          doc.setTextColor(0, 0, 0);
          yPos += 2;
          
          doc.setDrawColor(ATMANTAN_GOLD);
          doc.setLineWidth(0.2);
          doc.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 5;
          
          lastDate = null; // Reset to show date after page break
        }

        doc.setFontSize(8);
        doc.setFont('Roboto', 'normal');
        doc.setTextColor(0, 0, 0);

        // Day/Date column - only show on first booking of each day (merged cell style)
        // Display on one line: "DAY 1 14 OCT 2025" (no separator)
        if (lastDate !== date) {
          doc.setFont('Roboto', 'bold');
          doc.setFontSize(8);
          let dayDateText = '';
          if (dayNum) {
            dayDateText = `DAY ${dayNum}`;
          }
          doc.text(dayDateText, colX1, yPos);
          
          // Add date with space (no separator)
          const dayTextWidth = doc.getTextWidth(dayDateText);
          doc.setFont('Roboto', 'normal');
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);
          doc.text(formatDateForPDF(date), colX1 + dayTextWidth + 3, yPos);
          doc.setTextColor(0, 0, 0);
          lastDate = date;
        }

        // Time column - consistent font size, no line break
        doc.setFontSize(8);
        doc.setFont('Roboto', 'normal');
        const timeText = formatTimeRange(booking.startTime, booking.endTime);
        doc.text(timeText, colX2, yPos);

        // Treatment column - consistent font size
        doc.setFontSize(8);
        doc.setFont('Roboto', 'bold');
        const maxTreatmentWidth = colX4 - colX3 - 5;
        const treatmentText = doc.splitTextToSize(formatDisplayTreatmentName(booking.treatment), maxTreatmentWidth);
        doc.text(treatmentText[0], colX3, yPos);
        
        // Location column - consistent font size
        doc.setFontSize(8);
        doc.setFont('Roboto', 'normal');
        doc.text(booking.location || '', colX4, yPos);
        
        // Only show orientation for non-standard items
        if (!booking.isStandard) {
          yPos += 3.5;
          // Orientation - smaller font size (7 instead of 8)
          doc.setFontSize(7);
          doc.setFont('Roboto', 'normal');
          doc.setTextColor(120, 120, 120);
          doc.text('Orientation: 10 mins before', colX3, yPos);
          doc.setTextColor(0, 0, 0);
          yPos += 5;
        } else {
          yPos += 7; // Just spacing for standard items
        }
      });
    });

    // Add new page for All Available Activities
    doc.addPage();
    yPos = margin;

    // Activities Table Title
    doc.setFontSize(14);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(176, 152, 88);
    doc.text('All Available Activities', margin, yPos);
    yPos += 8;

    // Get activity dates and time ranges based on layout
    const activityDatesCard = getActivityDates();
    const timeRangesCard = activityLayout === 'grid' ? getUniqueTimeRanges() : getFixedHourlySlots();
    const getActivitiesFuncCard = activityLayout === 'grid' ? getActivitiesForTimeRange : null;

    if (activityDatesCard.length > 0 && timeRangesCard.length > 0) {
      // Table header
      doc.setFontSize(7);
      doc.setFont('Roboto', 'bold');
      doc.setTextColor(0, 0, 0);
      
      const timeColWidth = activityLayout === 'grid' ? 35 : 30;
      const dateColWidth = (pageWidth - 2 * margin - timeColWidth) / activityDatesCard.length;
      
      // Draw header row
      doc.text('FROM TIME - TO TIME', margin + 2, yPos);
      activityDatesCard.forEach((date, index) => {
        const x = margin + timeColWidth + index * dateColWidth;
        const headerText = formatDateHeader(date);
        doc.text(headerText, x + 2, yPos);
      });
      yPos += 3;
      
      // Header divider
      doc.setDrawColor(0, 0, 0);
        doc.setLineWidth(0.3);
        doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 2;
      
      // Table rows
      doc.setFont('Roboto', 'normal');
      doc.setFontSize(5);
      
      timeRangesCard.forEach((timeRange) => {
        const [startTime, endTime] = timeRange.split('-');
        const timeText = `${formatTimeForTable(startTime)} - ${formatTimeForTable(endTime)}`;
        
        // Check if we need a new page
        if (yPos > pageHeight - 40) {
          doc.addPage();
          yPos = margin;
          
          // Repeat header on new page
          doc.setFont('Roboto', 'bold');
          doc.setFontSize(7);
          doc.text('FROM TIME - TO TIME', margin + 2, yPos);
          activityDatesCard.forEach((date, index) => {
            const x = margin + timeColWidth + index * dateColWidth;
            doc.text(formatDateHeader(date), x + 2, yPos);
          });
          yPos += 3;
          doc.line(margin, yPos, pageWidth - margin, yPos);
          yPos += 2;
          doc.setFont('Roboto', 'normal');
          doc.setFontSize(5);
        }
        
        let maxRowHeight = 8;
        
        // Time column
        doc.setFontSize(6);
        doc.setFont('Roboto', 'bold');
        doc.text(timeText, margin + 1, yPos + 3);
        doc.setFont('Roboto', 'normal');
        doc.setFontSize(5);
        
        // Activity columns
        activityDatesCard.forEach((date, index) => {
          const x = margin + timeColWidth + index * dateColWidth;
          const activities = activityLayout === 'grid' 
            ? getActivitiesFuncCard(date, timeRange)
            : getActivitiesStartingInSlot(date, timeRange.split('-')[0], timeRange.split('-')[1]);
          
          if (activities.length > 0) {
            let cellY = yPos + 3;
            activities.forEach((activity, actIndex) => {
              if (actIndex > 0) {
                cellY += 2;
              }
              
              // Activity name with endurance rating (bold)
              doc.setFont('Roboto', 'bold');
              let activityNameWithStarsCard = activity.activity_name;
              if (activity.endurance_rating) {
                const starsCard = '\u2605'.repeat(activity.endurance_rating);
                activityNameWithStarsCard = `${starsCard} ${activity.activity_name}`;
              }
              doc.text(activityNameWithStarsCard, x + 2, cellY, { maxWidth: dateColWidth - 4 });
              cellY += 2.5;
              
              // Show actual time for all views
              doc.setFont('Roboto', 'normal');
              doc.setTextColor(80, 80, 80);
              const timeText = `${activity.start_time?.substring(0, 5)} - ${activity.end_time?.substring(0, 5)}`;
              doc.text(timeText, x + 2, cellY, { maxWidth: dateColWidth - 4 });
              cellY += 2.5;
              
              // Location
              doc.setFont('Roboto', 'normal');
              doc.setTextColor(100, 100, 100);
              const locationText = `@ ${activity.room_name || ''}`;
              doc.text(locationText, x + 2, cellY, { maxWidth: dateColWidth - 4 });
              cellY += 2.5;
              
              doc.setTextColor(0, 0, 0);
            });
            const cellHeight = cellY - (yPos + 3) + 2;
            maxRowHeight = Math.max(maxRowHeight, cellHeight);
          }
        });
        
        yPos += maxRowHeight;
        
        // Draw row divider
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.1);
        doc.line(margin, yPos, pageWidth - margin, yPos);
        yPos += 1;
      });
      
        yPos += 5;
    } else {
      doc.setFontSize(8);
      doc.setFont('Roboto', 'italic');
      doc.setTextColor(100, 100, 100);
      doc.text('No activities available for the selected date range.', margin, yPos);
      yPos += 10;
    }

    // Add information note below activities
    yPos += 8;
    
    // Check if we need a new page for the notes
    if (yPos > pageHeight - 60) {
      doc.addPage();
      yPos = margin;
    }
    
    doc.setFontSize(9);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Important Information:', margin, yPos);
    yPos += 6;
    
    doc.setFont('Roboto', 'normal');
    doc.setFontSize(8);
    const lineSpacing = 4.5;
    const bulletOffset = 3;
    
    // Note 1
    doc.text('\u2022', margin, yPos);
    const note1ListPDF = 'Choose Classes That Suit Your Fitness Level (\u2605BEGINNER \u2014\u2605\u2605 INTERMEDIATE \u2014\u2605\u2605\u2605ADVANCE) All Classes Have Been Marked With Stars As Per Their Competence Level.';
    const note1ListLines = doc.splitTextToSize(note1ListPDF, pageWidth - 2 * margin - bulletOffset);
    doc.text(note1ListLines, margin + bulletOffset, yPos);
    yPos += note1ListLines.length * lineSpacing + 3;
    
    // Note 2
    doc.text('\u2022', margin, yPos);
    const note2ListPDF = 'Report (5min Early) To All The Group Activities, So That You Can Setup Mats, follow INITIAL-IMPORTANT-INSTRUCTIONS OF The Session, Warmup / Loosening Practices, etc & Get The Completeness Of The Group Activity.';
    const note2ListLines = doc.splitTextToSize(note2ListPDF, pageWidth - 2 * margin - bulletOffset);
    doc.text(note2ListLines, margin + bulletOffset, yPos);
    yPos += note2ListLines.length * lineSpacing + 3;
    
    // Note 3
    doc.text('\u2022', margin, yPos);
    const note3ListPDF = 'Comfortable Fitness Gear Are Mandatory For All Fitness Activities \u2014 Indoor Sports Shoes, Track Pants/Sports Trouser, Sports T-Shirt.';
    const note3ListLines = doc.splitTextToSize(note3ListPDF, pageWidth - 2 * margin - bulletOffset);
    doc.text(note3ListLines, margin + bulletOffset, yPos);
    yPos += note3ListLines.length * lineSpacing + 3;
    
    // Note 4
    doc.text('\u2022', margin, yPos);
    const note4ListPDF = 'Personal Kriya Kits Are Available For Purchase @Wellness.';
    const note4ListLines = doc.splitTextToSize(note4ListPDF, pageWidth - 2 * margin - bulletOffset);
    doc.text(note4ListLines, margin + bulletOffset, yPos);
    yPos += note4ListLines.length * lineSpacing + 8;

    // Add new page for Footer with General Instructions, Doctor Recommendations, and Wellness Guidelines
    doc.addPage();
    yPos = margin;

    // Footer Title
    doc.setFontSize(14);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(176, 152, 88);
    doc.text('Important Information', margin, yPos);
    yPos += 8;

    // General Instructions
    doc.setFontSize(10);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('General Instructions:', margin, yPos);
    yPos += 6;

    doc.setFontSize(8);
    doc.setFont('Roboto', 'normal');
    const maxWidth = pageWidth - (margin * 2);
    GENERAL_INSTRUCTIONS.forEach((instruction) => {
      doc.text('•', margin + 2, yPos);
      const lines = doc.splitTextToSize(instruction, maxWidth - 5);
      doc.text(lines, margin + 5, yPos);
      yPos += lines.length * 4.5;
    });
    yPos += 4;

    doc.setFontSize(8);
    doc.setFont('Roboto', 'italic');
    doc.setTextColor(100, 100, 100);
    doc.text('Doctor recommendations are listed on each day\'s itinerary page.', margin, yPos);
    yPos += 8;
    doc.setTextColor(0, 0, 0);

    // Wellness Guidelines Section
    doc.setFontSize(10);
    doc.setFont('Roboto', 'bold');
    doc.setTextColor(0, 0, 0);
    doc.text('Wellness Guidelines:', margin, yPos);
    yPos += 6;

    doc.setFontSize(8);
    doc.setFont('Roboto', 'normal');
    WELLNESS_GUIDELINES.forEach((guideline) => {
      doc.text('•', margin + 2, yPos);
      const lines = doc.splitTextToSize(guideline, maxWidth - 5);
      doc.text(lines, margin + 5, yPos);
      yPos += lines.length * 4.5;
    });

    // Page footer
    doc.setFontSize(7);
    doc.setFont('Roboto', 'normal');
    doc.setTextColor(150, 150, 150);
    doc.text('Generated by Atmantan Wellness Scheduler', pageWidth / 2, pageHeight - 8, { align: 'center' });

    // Save PDF
    const filename = dayFilter === 'all' 
      ? `${guestInfo?.guestName || 'Guest'}_Full_Itinerary_List.pdf`
      : `${guestInfo?.guestName || 'Guest'}_Day${dayFilter}_Itinerary_List.pdf`;
    doc.save(filename);
  };

  // Main PDF generation function - uses current view mode
  const generatePDF = async (dayFilter = 'all') => {
    if (viewMode === 'list') {
      await generateListPDF(dayFilter);
    } else {
      await generateCardPDF(dayFilter);
    }
  };

  // Direct print function
  const handleDirectPrint = (dayFilter = 'all') => {
    const printWindow = window.open('', '_blank');
    
    // Filter bookings by day
    const datesToInclude = dayFilter === 'all' 
      ? Object.keys(bookingsByDate) 
      : Object.keys(bookingsByDate).filter(date => getDayNumber(date) === parseInt(dayFilter));

    if (viewMode === 'list') {
      // List/Table Print Format
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8" />
          ${getPrintFaviconTag()}
          <title>${guestInfo?.guestName || 'Guest'} - Itinerary</title>
          <style>
            @media print {
              @page { 
                size: A4;
                margin: 15mm;
              }
              body { margin: 0; }
            }
            
          body {
            font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
            padding: 20px;
            color: #000;
          }
            
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 20px;
            }
            
            .header-left h1 {
              margin: 0 0 15px 0;
              font-size: 22px;
              font-weight: 600;
              color: #B09858;
            }
            
            .guest-info-grid {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 10px;
              margin-bottom: 15px;
            }
            
            .info-item {
              display: flex;
              flex-direction: column;
            }
            
            .info-label {
              color: #000;
              font-size: 10px;
              font-weight: 600;
              margin-bottom: 2px;
            }
            
            .info-value {
              font-size: 12px;
              font-weight: 600;
              color: #000;
            }
            
            .header-right img {
              height: 40px;
              width: auto;
            }
            
            .divider {
              border-top: 2px solid #B09858;
              margin: 20px 0;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 20px;
            }
            
            th {
              text-align: left;
              color: #000;
              font-size: 11px;
              font-weight: 600;
              padding: 8px 5px;
              border-bottom: 1px solid #C7AE6E;
            }
            
            td {
              padding: 8px 5px;
              vertical-align: top;
              font-size: 12px;
            }
            
          .day-date-col {
            font-weight: bold;
            font-size: 12px;
            width: 120px;
          }
          
          .date-sub-text {
            font-weight: normal;
            color: #666;
            font-size: 12px;
            margin-left: 5px;
          }
          
          .time-col {
            font-family: 'Roboto Mono', monospace;
            font-size: 12px;
            width: 90px;
            white-space: nowrap;
          }
          
          .service-col {
            font-weight: bold;
          }
          
          .orientation {
            color: #888;
            font-size: 10px;
            margin-top: 2px;
            font-weight: normal;
          }
          
          .location-col {
            width: 150px;
          }

          .icon-col {
            width: 28px;
            text-align: center;
          }

          .treatment-descriptions {
            margin-top: 24px;
            padding-top: 16px;
            border-top: 1px solid #e0e0e0;
          }

          .treatment-descriptions-title,
          .doctor-recommendations-title {
            font-size: 13px;
            font-weight: 600;
            color: #B09858;
            margin: 0 0 12px 0;
          }

          .treatment-descriptions-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          }

          .treatment-description-name {
            font-size: 12px;
            font-weight: 600;
            margin-bottom: 4px;
          }

          .treatment-description-text {
            font-size: 11px;
            color: #666;
            line-height: 1.5;
          }

          .doctor-recommendations {
            margin-top: 24px;
            padding-top: 16px;
            border-top: 1px solid #e0e0e0;
          }

          .doctor-recommendations-list {
            margin: 0;
            padding-left: 20px;
            font-size: 12px;
            line-height: 1.5;
            list-style: none;
          }

          .task-checkbox {
            display: inline-block;
            width: 14px;
            margin-right: 8px;
            font-family: monospace;
          }
            
            .activities-section {
              margin-top: 40px;
              page-break-before: always;
            }
            
            .activities-table {
              width: 100%;
              border-collapse: collapse;
              margin-top: 15px;
              font-size: 10px;
            }
            
            .activities-table th,
            .activities-table td {
              border: 1px solid #ddd;
              padding: 6px;
              text-align: left;
            }
            
            .activities-table th {
              background-color: #f8f8f8;
              font-weight: 600;
              font-size: 11px;
            }
            
            .activities-table td {
              vertical-align: top;
            }
            
            .footer-section {
              margin-top: 40px;
              page-break-before: always;
            }
            
            .footer-title {
              font-size: 18px;
              font-weight: 600;
              color: #B09858;
              margin-bottom: 20px;
            }
            
            .footer-subtitle {
              font-size: 14px;
              font-weight: 600;
              color: #000;
              margin-top: 15px;
              margin-bottom: 8px;
            }
            
            .footer-placeholder {
              font-style: italic;
              color: #666;
              font-size: 11px;
            }
            
            .guidelines-list {
              list-style: disc;
              padding-left: 20px;
              margin: 10px 0;
            }
            
            .guidelines-list li {
              margin-bottom: 8px;
              font-size: 12px;
              line-height: 1.5;
            }
            
            .page-footer {
              text-align: center;
              color: #999;
              font-size: 10px;
              margin-top: 40px;
            }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="header-left">
              <h1>Guest Itinerary</h1>
              <div class="guest-info-grid">
                <div class="info-item">
                  <span class="info-label">Guest Name</span>
                  <span class="info-value">${guestInfo?.guestName || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Room No</span>
                  <span class="info-value">${guestInfo?.room || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Guest ID</span>
                  <span class="info-value">${guestInfo?.guestId || 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Check-in Date</span>
                  <span class="info-value">${guestInfo?.checkIn ? formatDate(guestInfo.checkIn) : 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Check-out Date</span>
                  <span class="info-value">${guestInfo?.checkOut ? formatDate(guestInfo.checkOut) : 'N/A'}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Retreat Name</span>
                  <span class="info-value">${guestInfo?.retreatName || 'N/A'}${guestInfo?.checkIn && guestInfo?.checkOut ? ` (${calculateNights(guestInfo.checkIn, guestInfo.checkOut)} Nights)` : ''}</span>
                </div>
                <div class="info-item">
                  <span class="info-label">Consulting Doctor</span>
                  <span class="info-value">${guestInfo?.consultingDoctor || 'N/A'}</span>
                </div>
              </div>
            </div>
            <div class="header-right">
              <img src="${getPrintAssetUrl('/Atmantan.png')}" alt="Atmantan" />
            </div>
          </div>
          
          <div class="divider"></div>
          
        <table>
          <thead>
            <tr>
              <th class="day-date-col">Day / Date</th>
              <th class="icon-col"></th>
              <th class="time-col">Time</th>
              <th class="service-col">Treatment / Orientation</th>
              <th class="location-col">Location</th>
            </tr>
          </thead>
          <tbody>
            ${datesToInclude.map((date) => {
              const dayBookings = bookingsByDate[date];
              const dayNum = getDayNumber(date);
              const rows = dayBookings.map((booking, index) => `
                <tr>
                  <td class="day-date-col">
                    ${index === 0 ? `DAY ${dayNum} <span class="date-sub-text">${formatDateForPDF(date)}</span>` : ''}
                  </td>
                  <td class="icon-col">${getActivityIconSVG(booking.treatment)}</td>
                  <td class="time-col">${formatTimeRange(booking.startTime, booking.endTime)}</td>
                  <td class="service-col">
                    ${formatDisplayTreatmentName(booking.treatment)}
                    ${!booking.isStandard ? '<div class="orientation">Orientation: 10 mins before</div>' : ''}
                  </td>
                  <td class="location-col">${booking.location || ''}</td>
                </tr>
              `).join('');

              const descriptionsRow = renderPrintTreatmentDescriptionsHTML(dayBookings)
                ? `<tr><td colspan="5">${renderPrintTreatmentDescriptionsHTML(dayBookings)}</td></tr>`
                : '';

              const tasksRow = renderPrintDoctorRecommendationsHTML(dayNum)
                ? `<tr><td colspan="5">${renderPrintDoctorRecommendationsHTML(dayNum)}</td></tr>`
                : '';

              return rows + descriptionsRow + tasksRow;
            }).join('')}
          </tbody>
        </table>
          
          <div class="page-break"></div>
          
          <div class="activities-section">
            <h2 class="footer-title">All Available Activities</h2>
            
            ${(() => {
              const activityDates = getActivityDates();
              const timeRanges = activityLayout === 'grid' ? getUniqueTimeRanges() : getFixedHourlySlots();
              const getActivitiesFuncPrint = activityLayout === 'grid' ? getActivitiesForTimeRange : null;
              
              if (activityDates.length === 0 || timeRanges.length === 0) {
                return '<p style="font-style: italic; color: #666;">No activities available for the selected date range.</p>';
              }
              
              return `
                <table class="activities-table">
                  <thead>
                    <tr>
                      <th style="width: 150px;">${activityLayout === 'grid' ? 'HOURLY SLOT' : 'ACTIVITY TIME'}</th>
                      ${activityDates.map(date => `<th>${formatDateHeader(date)}</th>`).join('')}
                    </tr>
                  </thead>
                  <tbody>
                    ${timeRanges.map(timeRange => {
                      const [startTime, endTime] = timeRange.split('-');
                      const timeText = `${formatTimeForTable(startTime)} - ${formatTimeForTable(endTime)}`;
                      return `
                        <tr>
                          <td style="font-weight: 600;">${timeText}</td>
                          ${activityDates.map(date => {
                            const activities = activityLayout === 'grid'
                              ? getActivitiesFuncPrint(date, timeRange)
                              : getActivitiesStartingInSlot(date, timeRange.split('-')[0], timeRange.split('-')[1]);
                            if (activities.length === 0) {
                              return '<td></td>';
                            }
                            return `<td style="font-size: 10px; padding: 8px;">
                              ${activities.map(activity => {
                                const stars = activity.endurance_rating ? '★'.repeat(activity.endurance_rating) + ' ' : '';
                                return `
                                <div style="margin-bottom: 8px;">
                                  <div style="font-weight: bold; margin-bottom: 2px;">${stars}${activity.activity_name}</div>
                                  <div style="color: #666; font-size: 9px; margin-bottom: 2px;">${activity.start_time?.substring(0, 5)} - ${activity.end_time?.substring(0, 5)}</div>
                                  <div style="color: #666; font-size: 9px; margin-bottom: 2px;">@ ${activity.room_name || ''}</div>
                                </div>
                              `;}).join('')}
                            </td>`;
                          }).join('')}
                        </tr>
                      `;
                    }).join('')}
                  </tbody>
                </table>
              `;
            })()}
            
            <div style="margin-top: 30px; padding: 15px; background-color: #f9f9f9; border: 1px solid #ddd; border-radius: 4px;">
              <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 10px;">Important Information:</h3>
              <ul style="list-style: none; padding: 0; margin: 0; font-size: 11px; line-height: 1.6;">
                <li style="margin-bottom: 8px;">• Choose Classes That Suit Your Fitness Level (★BEGINNER —★★ INTERMEDIATE —★★★ADVANCE) All Classes Have Been Marked With Stars As Per Their Competence Level.</li>
                <li style="margin-bottom: 8px;">• Report (5min Early) To All The Group Activities, So That You Can Setup Mats, follow INITIAL-IMPORTANT-INSTRUCTIONS OF The Session, Warmup / Loosening Practices, etc & Get The Completeness Of The Group Activity.</li>
                <li style="margin-bottom: 8px;">• Comfortable Fitness Gear Are Mandatory For All Fitness Activities — Indoor Sports Shoes, Track Pants/Sports Trouser, Sports T-Shirt.</li>
                <li style="margin-bottom: 8px;">• Personal Kriya Kits Are Available For Purchase @Wellness.</li>
              </ul>
            </div>
          </div>
          
          <div class="page-break"></div>
          
          <div class="footer-section">
            <h2 class="footer-title">Important Information</h2>
            
            <h3 class="footer-subtitle">General Instructions:</h3>
            <ul class="guidelines-list">
              ${GENERAL_INSTRUCTIONS.map((item) => `<li>${item}</li>`).join('')}
            </ul>
            
            <p class="footer-placeholder" style="margin-top: 16px; font-style: normal;">
              Doctor recommendations are listed on each day&apos;s itinerary page.
            </p>
            
            <h3 class="footer-subtitle">Wellness Guidelines:</h3>
            <ul class="guidelines-list">
              ${WELLNESS_GUIDELINES.map((item) => `<li>${item}</li>`).join('')}
            </ul>
          </div>
          
          <div class="page-footer">
            Generated by Atmantan Wellness Scheduler
          </div>
        </body>
        </html>
      `);
    } else {
      // Card Print Format
      const cardHTML = datesToInclude.map((date) => {
        const dayBookings = bookingsByDate[date];
        const dayNum = getDayNumber(date);
        
        return `
          <div class="page-break">
            <div class="header">
              <div class="header-left">
                <h1>${guestInfo?.guestName || 'Guest'}</h1>
                <div class="room">Room no: ${guestInfo?.room || 'N/A'}</div>
                <div class="details">Guest UID: ${guestInfo?.guestId || 'N/A'}</div>
                <div class="details">Retreat: ${guestInfo?.retreatName || 'N/A'}${guestInfo?.checkIn && guestInfo?.checkOut ? ` (${calculateNights(guestInfo.checkIn, guestInfo.checkOut)} Nights)` : ''}</div>
              </div>
              <div class="header-right">
                <img src="${getPrintAssetUrl('/Atmantan.png')}" alt="Atmantan" />
                <div class="header-day-block">
                  ${dayNum ? `<div class="day-number">DAY ${dayNum}</div>` : ''}
                  <div class="date">Date: ${formatDate(date)}</div>
                  <div class="doctor">Consulting Doctor: ${guestInfo?.consultingDoctor || 'N/A'}</div>
                </div>
              </div>
            </div>
            
            <div class="divider"></div>
            
            <div class="bookings">
            ${dayBookings.map((booking) => renderBookingCardHTML(booking)).join('')}
            </div>

            ${renderPrintTreatmentDescriptionsHTML(dayBookings)}
            ${renderPrintDoctorRecommendationsHTML(dayNum)}
          </div>
        `;
      }).join('');
      
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8" />
          ${getPrintFaviconTag()}
          <title>${guestInfo?.guestName || 'Guest'} - Itinerary</title>
          <style>
            @media print {
              @page { 
                size: A4;
                margin: 15mm;
              }
              .page-break {
                page-break-after: always;
              }
              .page-break:last-child {
                page-break-after: auto;
              }
            }
            
          body {
            font-family: 'Roboto', 'Helvetica', 'Arial', sans-serif;
            color: #000;
          }
            
            .page-break {
              padding: 20px;
            }
            
            .header {
              display: flex;
              justify-content: space-between;
              align-items: flex-start;
              margin-bottom: 20px;
            }
            
            .header-left h1 {
              margin: 0 0 10px 0;
              font-size: 28px;
              color: #000;
              font-weight: normal;
            }
            
            .header-left .room {
              font-size: 14px;
              margin-bottom: 8px;
            }
            
            .header-left .details {
              color: #000;
              font-size: 12px;
              margin: 4px 0;
            }
            
            .header-right {
              text-align: right;
              display: flex;
              flex-direction: column;
              align-items: flex-end;
            }
            
            .header-right img {
              height: 35px;
              width: auto;
              margin-bottom: 10px;
            }

            .header-day-block {
              text-align: right;
            }
            
            .day-number {
              font-size: 20px;
              font-weight: bold;
              margin: 0 0 4px 0;
            }
            
            .date, .doctor {
              font-size: 12px;
              color: #000;
              margin: 0 0 3px 0;
            }
            
            .divider {
              border-top: 2px solid #B09858;
              margin: 12px 0;
            }
            
          .bookings {
            column-count: 2;
            column-gap: 24px;
            column-fill: auto;
          }

          .booking-card {
            break-inside: avoid;
            page-break-inside: avoid;
            margin: 6px 0;
            padding-bottom: 6px;
            border-bottom: 1px solid #C7AE6E;
            display: flex;
            gap: 10px;
            align-items: flex-start;
          }
          
          .booking-card:last-child {
            border-bottom: none;
            margin-bottom: 0;
            padding-bottom: 0;
          }

          .booking-main {
            flex: 1;
            min-width: 0;
            display: flex;
            justify-content: space-between;
            gap: 12px;
            align-items: flex-start;
          }

          .icon-wrapper {
            flex-shrink: 0;
            padding-top: 1px;
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .icon-wrapper svg {
            display: block;
          }

          .booking-content {
            flex: 1;
            min-width: 0;
          }

          .booking-location {
            flex-shrink: 0;
            text-align: right;
            font-size: 11px;
            line-height: 1.35;
            color: #333;
            max-width: 42%;
          }
          
          .time {
            font-size: 11px;
            color: #666;
            margin-bottom: 2px;
          }
          
          .treatment {
            font-size: 13px;
            font-weight: bold;
            margin-bottom: 2px;
          }
          
          .orientation {
            font-size: 10px;
            color: #888;
            margin-bottom: 2px;
          }

          .treatment-descriptions {
            margin-top: 24px;
            padding-top: 16px;
            border-top: 1px solid #e0e0e0;
          }

          .treatment-descriptions-title,
          .doctor-recommendations-title {
            font-size: 14px;
            font-weight: 600;
            color: #B09858;
            margin: 0 0 12px 0;
          }

          .treatment-descriptions-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 16px;
          }

          .treatment-description-name {
            font-size: 13px;
            font-weight: 600;
            margin-bottom: 4px;
          }

          .treatment-description-text {
            font-size: 12px;
            color: #666;
            line-height: 1.5;
          }

          .doctor-recommendations {
            margin-top: 24px;
            padding-top: 16px;
            border-top: 1px solid #e0e0e0;
          }

          .doctor-recommendations-list {
            margin: 0;
            padding-left: 20px;
            font-size: 13px;
            line-height: 1.6;
          }
          
          .footer-section {
            padding: 20px;
            page-break-before: always;
          }
          
          .footer-title {
            font-size: 20px;
            font-weight: 600;
            color: #B09858;
            margin-bottom: 20px;
          }
          
          .footer-subtitle {
            font-size: 16px;
            font-weight: 600;
            color: #000;
            margin-top: 15px;
            margin-bottom: 8px;
          }
          
          .footer-placeholder {
            font-style: italic;
            color: #666;
            font-size: 13px;
          }
          
          .guidelines-list {
            list-style: disc;
            padding-left: 20px;
            margin: 10px 0;
          }
          
          .guidelines-list li {
            margin-bottom: 10px;
            font-size: 13px;
            line-height: 1.6;
          }
          
          .page-footer {
            text-align: center;
            color: #999;
            font-size: 11px;
            margin-top: 30px;
          }
          </style>
        </head>
        <body>
          ${cardHTML}
          
          <div class="activities-section" style="page-break-before: always; padding: 20px;">
            <h2 class="footer-title">All Available Activities</h2>
            
            ${(() => {
              const activityDates = getActivityDates();
              const timeRanges = activityLayout === 'grid' ? getUniqueTimeRanges() : getFixedHourlySlots();
              const getActivitiesFuncCardPrint = activityLayout === 'grid' ? getActivitiesForTimeRange : null;
              
              if (activityDates.length === 0 || timeRanges.length === 0) {
                return '<p style="font-style: italic; color: #666;">No activities available for the selected date range.</p>';
              }
              
              return `
                <table class="activities-table" style="width: 100%; border-collapse: collapse; margin-top: 10px;">
                  <thead>
                    <tr>
                      <th style="border: 1px solid #ddd; padding: 8px; background-color: #f8f8f8; text-align: left; width: 150px;">${activityLayout === 'grid' ? 'HOURLY SLOT' : 'ACTIVITY TIME'}</th>
                      ${activityDates.map(date => `<th style="border: 1px solid #ddd; padding: 8px; background-color: #f8f8f8; text-align: left;">${formatDateHeader(date)}</th>`).join('')}
                    </tr>
                  </thead>
                  <tbody>
                    ${timeRanges.map(timeRange => {
                      const [startTime, endTime] = timeRange.split('-');
                      const timeText = `${formatTimeForTable(startTime)} - ${formatTimeForTable(endTime)}`;
                      return `
                        <tr>
                          <td style="border: 1px solid #ddd; padding: 8px; font-weight: 600;">${timeText}</td>
                          ${activityDates.map(date => {
                            const activities = activityLayout === 'grid'
                              ? getActivitiesFuncCardPrint(date, timeRange)
                              : getActivitiesStartingInSlot(date, timeRange.split('-')[0], timeRange.split('-')[1]);
                            if (activities.length === 0) {
                              return '<td style="border: 1px solid #ddd; padding: 8px;"></td>';
                            }
                            return `<td style="border: 1px solid #ddd; padding: 8px; font-size: 10px;">
                              ${activities.map(activity => {
                                const starsCard = activity.endurance_rating ? '★'.repeat(activity.endurance_rating) + ' ' : '';
                                return `
                                <div style="margin-bottom: 8px;">
                                  <div style="font-weight: bold; margin-bottom: 2px;">${starsCard}${activity.activity_name}</div>
                                  <div style="color: #666; font-size: 9px; margin-bottom: 2px;">${activity.start_time?.substring(0, 5)} - ${activity.end_time?.substring(0, 5)}</div>
                                  <div style="color: #666; font-size: 9px; margin-bottom: 2px;">@ ${activity.room_name || ''}</div>
                                </div>
                              `;}).join('')}
                            </td>`;
                          }).join('')}
                        </tr>
                      `;
                    }).join('')}
                  </tbody>
                </table>
              `;
            })()}
            
            <div style="margin-top: 30px; padding: 15px; background-color: #f9f9f9; border: 1px solid #ddd; border-radius: 4px;">
              <h3 style="font-size: 14px; font-weight: bold; margin-bottom: 10px;">Important Information:</h3>
              <ul style="list-style: none; padding: 0; margin: 0; font-size: 11px; line-height: 1.6;">
                <li style="margin-bottom: 8px;">• Choose Classes That Suit Your Fitness Level (★BEGINNER —★★ INTERMEDIATE —★★★ADVANCE) All Classes Have Been Marked With Stars As Per Their Competence Level.</li>
                <li style="margin-bottom: 8px;">• Report (5min Early) To All The Group Activities, So That You Can Setup Mats, follow INITIAL-IMPORTANT-INSTRUCTIONS OF The Session, Warmup / Loosening Practices, etc & Get The Completeness Of The Group Activity.</li>
                <li style="margin-bottom: 8px;">• Comfortable Fitness Gear Are Mandatory For All Fitness Activities — Indoor Sports Shoes, Track Pants/Sports Trouser, Sports T-Shirt.</li>
                <li style="margin-bottom: 8px;">• Personal Kriya Kits Are Available For Purchase @Wellness.</li>
              </ul>
            </div>
          </div>
          
          <div class="footer-section">
            <h2 class="footer-title">Important Information</h2>
            
            <h3 class="footer-subtitle">General Instructions:</h3>
            <ul class="guidelines-list">
              ${GENERAL_INSTRUCTIONS.map((item) => `<li>${item}</li>`).join('')}
            </ul>
            
            <p class="footer-placeholder" style="margin-top: 16px; font-style: normal;">
              Doctor recommendations are listed on each day&apos;s itinerary page.
            </p>
            
            <h3 class="footer-subtitle">Wellness Guidelines:</h3>
            <ul class="guidelines-list">
              ${WELLNESS_GUIDELINES.map((item) => `<li>${item}</li>`).join('')}
            </ul>
            
            <div class="page-footer">
              Generated by Atmantan Wellness Scheduler
            </div>
          </div>
        </body>
        </html>
      `);
    }
    
    printWindow.document.close();
    
    // Wait for images to load before printing
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.print();
      }, 250);
    };
  };

  // Compact List/Table View - Simple columns, no icons
  const renderListView = () => {
    const datesToShow = selectedDay === 'all' 
      ? Object.keys(bookingsByDate) 
      : Object.keys(bookingsByDate).filter(date => getDayNumber(date) === parseInt(selectedDay));

    if (datesToShow.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            No bookings found for this selection
          </Typography>
        </Box>
      );
    }

    // Show guest info once at the top for "all days" view
    const showGuestHeaderOnce = selectedDay === 'all';

      return (
      <>
        {showGuestHeaderOnce && (
          <Box sx={{ 
            mb: 3,
            pb: 2,
            borderBottom: `2px solid ${ATMANTAN_GOLD}`
          }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
              alignItems: 'center'
          }}>
            <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.2rem' }}>
                {guestInfo?.guestName || 'Guest'}
              </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Room no: {guestInfo?.room}
                {guestInfo?.guestId ? ` | Guest ID: ${guestInfo.guestId}` : ''}
                {guestInfo?.retreatName ? ` | Retreat Name: ${guestInfo.retreatName}` : ''}
                  {guestInfo?.consultingDoctor && ` | Consulting Doctor: ${guestInfo.consultingDoctor}`}
              </Typography>
            </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: ATMANTAN_DARK_GOLD,
                  fontFamily: 'serif',
                  letterSpacing: 0.5,
                  fontSize: '1rem'
                }}
              >
                ātmantan
              </Typography>
            </Box>
          </Box>
        )}

        {/* Single table for all bookings */}
        {!showGuestHeaderOnce && datesToShow.map((date, dateIndex) => {
          const dayBookings = bookingsByDate[date];
          const dayNum = getDayNumber(date);

          return (
            <Box key={date} sx={{ mb: 3 }}>
              {/* Day header - simplified when showing all days */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                mb: 1.5,
                pb: 0.5,
                borderBottom: `1px solid ${ATMANTAN_GOLD}`
              }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                    {guestInfo?.guestName || 'Guest'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Room no: {guestInfo?.room} | Guest ID: {guestInfo?.guestId} | Retreat Name: {guestInfo?.retreatName}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
              {dayNum && (
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.95rem', display: 'inline', mr: 2 }}>
                  DAY {dayNum}
                </Typography>
              )}
                  <Typography variant="body2" color="text.secondary" sx={{ display: 'inline' }}>
                    {formatDate(date)}
              </Typography>
                  {guestInfo?.consultingDoctor && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Consulting Doctor: {guestInfo.consultingDoctor}
                    </Typography>
                  )}
            </Box>
          </Box>

              {/* Compact table - all info in single row */}
          <TableContainer>
            <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, py: 1, fontSize: '0.8rem', color: 'text.secondary' }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 1, fontSize: '0.8rem', color: 'text.secondary' }}>Treatment / Orientation</TableCell>
                      <TableCell sx={{ fontWeight: 600, py: 1, fontSize: '0.8rem', color: 'text.secondary' }}>Location</TableCell>
                    </TableRow>
                  </TableHead>
              <TableBody>
                {dayBookings.map((booking, index) => (
                      <TableRow 
                        key={booking.bookingId || booking.id || index} 
                        sx={{ 
                          '&:hover': { backgroundColor: `${ATMANTAN_GOLD}10` },
                          borderBottom: `1px solid #e0e0e0`
                        }}
                      >
                        <TableCell sx={{ py: 1.5, fontSize: '0.85rem', fontFamily: 'monospace' }}>
                        {formatTimeRange(booking.startTime, booking.endTime)}
                        </TableCell>
                        <TableCell sx={{ py: 1.5 }}>
                          <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                        {formatDisplayTreatmentName(booking.treatment)}
                      </Typography>
                          {!booking.isStandard && (
                            <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              Orientation: 10 mins before
                            </Typography>
                          )}
                    </TableCell>
                        <TableCell sx={{ py: 1.5, fontSize: '0.85rem' }}>
                          {booking.location}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {getDayTreatmentDescriptions(dayBookings).length > 0 && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: ATMANTAN_DARK_GOLD }}>
                    Treatments & Activities
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    {getDayTreatmentDescriptions(dayBookings).map((item) => (
                      <Box key={item.name}>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                          {item.description}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
              {(getDoctorRecommendationsForDay(dayNum).length > 0 || guestInfo?.bookingId) && (
                <Box sx={{ mt: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: ATMANTAN_DARK_GOLD }}>
                    Your doctor recommends the following for you:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0, listStyle: 'none' }}>
                    {renderDoctorRecommendationsText(dayNum)}
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}

        {/* Combined table when showing all days */}
        {showGuestHeaderOnce && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, py: 1, fontSize: '0.8rem', color: 'text.secondary', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>Day / Date</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1, fontSize: '0.8rem', color: 'text.secondary', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1, fontSize: '0.8rem', color: 'text.secondary', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>Treatment / Orientation</TableCell>
                  <TableCell sx={{ fontWeight: 600, py: 1, fontSize: '0.8rem', color: 'text.secondary', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>Location</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {datesToShow.map((date) => {
                  const dayBookings = bookingsByDate[date];
                  const dayNum = getDayNumber(date);

                  return dayBookings.map((booking, index) => (
                    <TableRow 
                      key={`${date}-${booking.bookingId || booking.id || index}`}
                      sx={{ 
                        '&:hover': { backgroundColor: `${ATMANTAN_GOLD}10` },
                        borderBottom: `1px solid #e0e0e0`
                      }}
                    >
                      <TableCell sx={{ py: 1.5, fontSize: '0.85rem', fontWeight: 500 }}>
                        {index === 0 && (
                          <>
                            {dayNum && (
                              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                DAY {dayNum}
                      </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                              {formatDate(date)}
                      </Typography>
                          </>
                        )}
                    </TableCell>
                      <TableCell sx={{ py: 1.5, fontSize: '0.85rem', fontFamily: 'monospace' }}>
                        {formatTimeRange(booking.startTime, booking.endTime)}
                      </TableCell>
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ fontSize: '0.85rem', fontWeight: 500 }}>
                          {formatDisplayTreatmentName(booking.treatment)}
                      </Typography>
                        {!booking.isStandard && (
                          <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                            Orientation: 10 mins before
                      </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ py: 1.5, fontSize: '0.85rem' }}>
                        {booking.location}
                    </TableCell>
                  </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </>
      );
  };

  // Card View with Icons
  const renderCardView = () => {
    const datesToShow = selectedDay === 'all' 
      ? Object.keys(bookingsByDate) 
      : Object.keys(bookingsByDate).filter(date => getDayNumber(date) === parseInt(selectedDay));

    if (datesToShow.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography color="text.secondary">
            No bookings found for this selection
          </Typography>
        </Box>
      );
    }

    // Show guest info once at the top for "all days" view
    const showGuestHeaderOnce = selectedDay === 'all';

    return (
      <>
        {showGuestHeaderOnce && (
          <Box sx={{ 
            mb: 3,
            pb: 2,
            borderBottom: `2px solid ${ATMANTAN_GOLD}`
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center'
            }}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.2rem' }}>
                  {guestInfo?.guestName || 'Guest'}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Room no: {guestInfo?.room} | Guest ID: {guestInfo?.guestId} | Retreat Name: {guestInfo?.retreatName}
                  {guestInfo?.consultingDoctor && ` | Consulting Doctor: ${guestInfo.consultingDoctor}`}
                </Typography>
              </Box>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: ATMANTAN_DARK_GOLD,
                  fontFamily: 'serif',
                  letterSpacing: 0.5,
                  fontSize: '1rem'
                }}
              >
                ātmantan
              </Typography>
            </Box>
          </Box>
        )}

        {datesToShow.map((date) => {
      const dayBookings = bookingsByDate[date];
      const dayNum = getDayNumber(date);

      return (
        <Box key={date} sx={{ mb: 4 }}>
              {/* Day Header */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 1.5,
            pb: 1,
            borderBottom: `2px solid ${ATMANTAN_GOLD}`
          }}>
                {!showGuestHeaderOnce && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                {guestInfo?.guestName || 'Guest'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Room no: {guestInfo?.room} | Guest ID: {guestInfo?.guestId} | Retreat Name: {guestInfo?.retreatName}
              </Typography>
            </Box>
                )}
                <Box sx={{ textAlign: 'right', minWidth: 180, ml: showGuestHeaderOnce ? 'auto' : 0 }}>
              {dayNum && (
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                  DAY {dayNum}
                </Typography>
              )}
                  <Typography variant="caption" color="text.secondary" display="block">
                Date: {formatDate(date)}
              </Typography>
                  {!showGuestHeaderOnce && guestInfo?.consultingDoctor && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Consulting Doctor: {guestInfo.consultingDoctor}
                    </Typography>
                  )}
            </Box>
          </Box>

              {/* Cards with Icons — two columns fill left first, then right on same page */}
          <Box sx={{ columnCount: 2, columnGap: 3, columnFill: 'auto' }}>
            {dayBookings.map((booking, index) => (
              <Box 
                key={booking.bookingId || booking.id || index}
                sx={{ 
                      display: 'grid',
                      gridTemplateColumns: '40px 1fr auto',
                  alignItems: 'start',
                  gap: 2,
                  py: 1.5,
                      breakInside: 'avoid',
                      borderBottom: index < dayBookings.length - 1 ? `1px solid ${ATMANTAN_GOLD}40` : 'none',
                      '&:hover': { backgroundColor: `${ATMANTAN_GOLD}10` }
                }}
              >
                {/* Icon */}
                <Box sx={{ flexShrink: 0 }}>
                  {getActivityIcon(booking.treatment)}
                </Box>

                    {/* Time & Treatment & Orientation */}
                    <Box>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'text.secondary' }}>
                    {formatTimeRange(booking.startTime, booking.endTime)}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mt: 0.2 }}>
                    {formatDisplayTreatmentName(booking.treatment)}
                  </Typography>
                      {!booking.isStandard && (
                        <Typography variant="caption" sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', mt: 0.2 }}>
                          Orientation: 10 mins before
                        </Typography>
                      )}
                </Box>

                {/* Location — right side of each row */}
                    <Box sx={{ textAlign: 'right', minWidth: 100, maxWidth: '42%' }}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Location
                  </Typography>
                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                        {booking.location}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>

          {getDayTreatmentDescriptions(dayBookings).length > 0 && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: ATMANTAN_DARK_GOLD }}>
                Treatments & Activities
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {getDayTreatmentDescriptions(dayBookings).map((item) => (
                  <Box key={item.name}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {item.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                      {item.description}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
          {(getDoctorRecommendationsForDay(dayNum).length > 0 || guestInfo?.bookingId) && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: '#fafafa', borderRadius: 1, border: '1px solid #eee' }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: ATMANTAN_DARK_GOLD }}>
                Your doctor recommends the following for you:
              </Typography>
              <Box component="ul" sx={{ pl: 2, m: 0, listStyle: 'none' }}>
                {renderDoctorRecommendationsText(dayNum)}
              </Box>
            </Box>
          )}
        </Box>
      );
        })}
      </>
    );
  };

  // Get available days for dropdown
  const availableDays = useMemo(() => {
    const days = Object.keys(bookingsByDate)
      .map(date => ({
        date,
        dayNum: getDayNumber(date)
      }))
      .filter(d => d.dayNum !== null)
      .sort((a, b) => a.dayNum - b.dayNum);
    return days;
  }, [bookingsByDate, guestInfo]);

  // Content to be rendered (shared between modal and embedded modes)
  const renderContent = () => (
    <>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
          <CircularProgress sx={{ color: ATMANTAN_GOLD }} />
        </Box>
      ) : (
        <>
          {/* Left Sidebar - 25% */}
          <Box sx={{ 
            width: '25%', 
            borderRight: '1px solid #e0e0e0',
            p: 3,
            overflow: 'auto',
            backgroundColor: '#fafafa'
          }}>
              {/* Guest Information */}
              {guestInfo && (
                <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #e0e0e0' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                        Guest Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {guestInfo.guestName || 'Guest'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                        Room No
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {guestInfo.room || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                        Check-in Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {guestInfo.checkIn ? formatDate(guestInfo.checkIn) : 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                        Check-out Date
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {guestInfo.checkOut ? formatDate(guestInfo.checkOut) : 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                        Guest ID
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {guestInfo.guestId || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                        Retreat Name
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {guestInfo.retreatName} {guestInfo.checkIn && guestInfo.checkOut && `(${calculateNights(guestInfo.checkIn, guestInfo.checkOut)} Nights)`}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600, display: 'block' }}>
                        Consulting Doctor
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {guestInfo.consultingDoctor || 'N/A'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Date Range Selector */}
              <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: ATMANTAN_DARK_GOLD }}>
                  Date Range
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  <TextField
                    label="Start Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={customStartDate}
                    onChange={(e) => setCustomStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    label="End Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={customEndDate}
                    onChange={(e) => setCustomEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    fullWidth
                    onClick={handleDateRangeChange}
                    disabled={!customStartDate || !customEndDate}
                    sx={{
                      backgroundColor: ATMANTAN_GOLD,
                      color: '#171717',
                      '&:hover': {
                        backgroundColor: ATMANTAN_DARK_GOLD
                      }
                    }}
                  >
                    Apply
                  </Button>
                </Box>
              </Box>

              {/* View Day Filter */}
              <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: ATMANTAN_DARK_GOLD }}>
                  View Day
                </Typography>
                <FormControl size="small" fullWidth>
          <Select
            value={selectedDay}
            onChange={(e) => setSelectedDay(e.target.value)}
          >
            <MenuItem value="all">All Days</MenuItem>
            {availableDays.map(({ date, dayNum }) => (
              <MenuItem key={date} value={dayNum}>
                Day {dayNum} ({formatDate(date)})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
              </Box>

            </Box>

            {/* Right Content Area - 75% */}
            <Box sx={{ 
              width: '75%', 
              display: 'flex',
              flexDirection: 'column'
            }}>
              {/* Sticky Header with View Mode and Actions */}
              <Box sx={{ 
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                backgroundColor: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                position: 'sticky',
                top: 0,
                zIndex: 10
              }}>
          <ButtonGroup size="small" variant="outlined">
            <Button
              onClick={() => setViewMode('list')}
              variant={viewMode === 'list' ? 'contained' : 'outlined'}
              startIcon={<ViewListIcon />}
              sx={{
                backgroundColor: viewMode === 'list' ? ATMANTAN_GOLD : 'transparent',
                color: viewMode === 'list' ? '#171717' : '#666',
                borderColor: ATMANTAN_GOLD,
                '&:hover': {
                  backgroundColor: viewMode === 'list' ? ATMANTAN_DARK_GOLD : 'rgba(199, 174, 110, 0.08)',
                  borderColor: ATMANTAN_GOLD
                }
              }}
            >
              List
            </Button>
            <Button
              onClick={() => setViewMode('card')}
              variant={viewMode === 'card' ? 'contained' : 'outlined'}
              startIcon={<ViewModuleIcon />}
              sx={{
                backgroundColor: viewMode === 'card' ? ATMANTAN_GOLD : 'transparent',
                color: viewMode === 'card' ? '#171717' : '#666',
                borderColor: ATMANTAN_GOLD,
                '&:hover': {
                  backgroundColor: viewMode === 'card' ? ATMANTAN_DARK_GOLD : 'rgba(199, 174, 110, 0.08)',
                  borderColor: ATMANTAN_GOLD
                }
              }}
            >
              Cards
            </Button>
          </ButtonGroup>

                <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
                    onClick={() => handleDirectPrint(selectedDay)}
                    startIcon={<PrintIcon />}
            variant="outlined"
            size="small"
            sx={{
              borderColor: ATMANTAN_GOLD,
              color: '#171717',
              '&:hover': {
                borderColor: ATMANTAN_DARK_GOLD,
                backgroundColor: 'rgba(199, 174, 110, 0.08)'
              }
            }}
          >
                    Print
          </Button>

        <Button 
                    onClick={async () => await generatePDF(selectedDay)}
                    startIcon={<DownloadIcon />}
                    variant="contained"
                    size="small"
          sx={{
                      backgroundColor: ATMANTAN_GOLD,
            color: '#171717',
            '&:hover': {
                        backgroundColor: ATMANTAN_DARK_GOLD
              }
            }}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

              {/* Scrollable Content */}
              <Box sx={{ 
                flex: 1,
                overflow: 'auto',
                p: 3
              }}>
            {viewMode === 'list' ? renderListView() : renderCardView()}
              
              {/* All Available Activities Section */}
              {allActivities.length > 0 && (
                <Box sx={{ mt: 4, mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: ATMANTAN_DARK_GOLD }}>
                      All Available Activities
                    </Typography>
                    <ButtonGroup size="small" variant="outlined">
        <Button 
                        onClick={() => setActivityLayout('grid')}
                        variant={activityLayout === 'grid' ? 'contained' : 'outlined'}
          sx={{
                          backgroundColor: activityLayout === 'grid' ? ATMANTAN_GOLD : 'transparent',
                          color: activityLayout === 'grid' ? '#171717' : '#666',
            borderColor: ATMANTAN_GOLD,
                          fontSize: '0.75rem',
            '&:hover': {
                            backgroundColor: activityLayout === 'grid' ? ATMANTAN_DARK_GOLD : 'rgba(199, 174, 110, 0.08)',
                            borderColor: ATMANTAN_GOLD
            }
          }}
        >
                        Grid View
        </Button>
                      <Button
                        onClick={() => setActivityLayout('timeslot')}
                        variant={activityLayout === 'timeslot' ? 'contained' : 'outlined'}
                        sx={{
                          backgroundColor: activityLayout === 'timeslot' ? ATMANTAN_GOLD : 'transparent',
                          color: activityLayout === 'timeslot' ? '#171717' : '#666',
                          borderColor: ATMANTAN_GOLD,
                          fontSize: '0.75rem',
                          '&:hover': {
                            backgroundColor: activityLayout === 'timeslot' ? ATMANTAN_DARK_GOLD : 'rgba(199, 174, 110, 0.08)',
                            borderColor: ATMANTAN_GOLD
                          }
                        }}
                      >
                        Timeslot View
                      </Button>
                    </ButtonGroup>
                  </Box>

                  {activityLayout === 'grid' ? (
                    // Grid Layout (actual activity times grouped)
                    <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: '140px' }}>Activity Time</TableCell>
                            {getActivityDates().map(date => (
                              <TableCell key={date} sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                                {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getUniqueTimeRanges().map(slotRange => {
                            const [startTime, endTime] = slotRange.split('-');
                            return (
                              <TableRow key={slotRange}>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                  {formatTimeForTable(startTime)} - {formatTimeForTable(endTime)}
                                </TableCell>
                                {getActivityDates().map(date => {
                                  const activities = getActivitiesForTimeRange(date, slotRange);
                                  return (
                                    <TableCell key={`${date}-${slotRange}`} sx={{ fontSize: '0.75rem', padding: '8px' }}>
                                      {activities.map((activity, index) => (
                                        <Box key={index} sx={{ 
                                          mb: index < activities.length - 1 ? 1 : 0
                                        }}>
                                          <Typography sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                            {activity.endurance_rating ? '★'.repeat(activity.endurance_rating) + ' ' : ''}
                                            {activity.activity_name}
                                          </Typography>
                                          <Typography sx={{ color: '#666', fontSize: '0.7rem' }}>
                                            {activity.start_time?.substring(0, 5)} - {activity.end_time?.substring(0, 5)}
                                          </Typography>
                                          <Typography sx={{ color: '#666', fontSize: '0.7rem' }}>
                                            @ {activity.room_name || ''}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    // Timeslot Layout (fixed 1-hour intervals showing activities that start in each slot)
                    <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', width: '140px' }}>Hourly Slot</TableCell>
                            {getActivityDates().map(date => (
                              <TableCell key={date} sx={{ fontWeight: 600, backgroundColor: '#f5f5f5', textAlign: 'center' }}>
                                {new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getFixedHourlySlots().map(timeRange => {
                            const [startTime, endTime] = timeRange.split('-');
                            // Calculate max activities for this timeslot across all dates
                            let maxActivitiesInSlot = 1;
                            getActivityDates().forEach(date => {
                              const activities = getActivitiesStartingInSlot(date, startTime, endTime);
                              maxActivitiesInSlot = Math.max(maxActivitiesInSlot, activities.length);
                            });
                            
                            return (
                              <TableRow key={timeRange} sx={{ height: `${40 + (maxActivitiesInSlot - 1) * 35}px` }}>
                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', verticalAlign: 'top', pt: 2 }}>
                                  {formatTimeForTable(startTime)}<br />-<br />{formatTimeForTable(endTime)}
                                </TableCell>
                                {getActivityDates().map(date => {
                                  const activities = getActivitiesStartingInSlot(date, startTime, endTime);
                                  return (
                                    <TableCell key={`${date}-${timeRange}`} sx={{ fontSize: '0.75rem', padding: '8px', verticalAlign: 'top' }}>
                                      {activities.map((activity, index) => (
                                        <Box key={index} sx={{ 
                                          mb: index < activities.length - 1 ? 1 : 0
                                        }}>
                                          <Typography sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
                                            {activity.endurance_rating ? '★'.repeat(activity.endurance_rating) + ' ' : ''}
                                            {activity.activity_name}
                                          </Typography>
                                          <Typography sx={{ color: '#666', fontSize: '0.7rem' }}>
                                            {activity.start_time?.substring(0, 5)} - {activity.end_time?.substring(0, 5)}
                                          </Typography>
                                          <Typography sx={{ color: '#666', fontSize: '0.7rem' }}>
                                            @ {activity.room_name || ''}
                                          </Typography>
                                        </Box>
                                      ))}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {/* Important Information Note */}
                  <Box sx={{ mt: 3, p: 2, backgroundColor: '#f9f9f9', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Important Information:</Typography>
                    <Box component="ul" sx={{ pl: 2, m: 0, fontSize: '0.8rem', lineHeight: 1.6 }}>
                      <Typography component="li" sx={{ mb: 0.5 }}>
                        Choose Classes That Suit Your Fitness Level (★BEGINNER —★★ INTERMEDIATE —★★★ADVANCE) All Classes Have Been Marked With Stars As Per Their Competence Level.
                      </Typography>
                      <Typography component="li" sx={{ mb: 0.5 }}>
                        Report (5min Early) To All The Group Activities, So That You Can Setup Mats, follow INITIAL-IMPORTANT-INSTRUCTIONS OF The Session, Warmup / Loosening Practices, etc & Get The Completeness Of The Group Activity.
                      </Typography>
                      <Typography component="li" sx={{ mb: 0.5 }}>
                        Comfortable Fitness Gear Are Mandatory For All Fitness Activities — Indoor Sports Shoes, Track Pants/Sports Trouser, Sports T-Shirt.
                      </Typography>
                      <Typography component="li">
                        Kriya Kits are available for loan at Shakthi Wellness Front Desk.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              {/* Footer with General Instructions, Doctor Recommendations, and Wellness Guidelines */}
              <Box sx={{ 
                mt: 4,
                pt: 3, 
                borderTop: '2px solid #e0e0e0',
                backgroundColor: '#fafafa',
                borderRadius: 1,
                p: 3
              }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: ATMANTAN_DARK_GOLD }}>
                    General Instructions:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    {GENERAL_INSTRUCTIONS.map((instruction) => (
                      <Typography key={instruction} component="li" variant="body2" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                        {instruction}
                      </Typography>
                    ))}
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: ATMANTAN_DARK_GOLD }}>
                    Your doctor recommends the following for you:
                  </Typography>
                  {doctorRecommendations.length > 0 ? (
                    <Box component="ul" sx={{ pl: 2, m: 0, listStyle: 'none' }}>
                      {renderDoctorRecommendationsText(
                        selectedDay !== 'all' ? parseInt(selectedDay, 10) : null
                      )}
                    </Box>
                  ) : (
                    renderDoctorRecommendationsText(
                      selectedDay !== 'all' ? parseInt(selectedDay, 10) : null
                    )
                  )}
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: ATMANTAN_DARK_GOLD }}>
                    Wellness Guidelines:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, m: 0 }}>
                    {WELLNESS_GUIDELINES.map((guideline) => (
                      <Typography key={guideline} component="li" variant="body2" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
                        {guideline}
                      </Typography>
                    ))}
                  </Box>
                </Box>
              </Box>
              </Box>
            </Box>
          </>
        )}
      </>
  );

  // Embedded mode - render content directly without Dialog wrapper
  if (embedded) {
    return (
      <Box sx={{ 
        display: 'flex', 
        height: '100%', 
        overflow: 'hidden',
        bgcolor: '#fff'
      }}>
        {renderContent()}
      </Box>
    );
  }

  // Modal mode - render with Dialog wrapper
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 'rgba(145, 158, 171, 0.2) 0px 20px 40px -4px',
          maxHeight: '90vh',
          height: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        borderBottom: '1px solid #e0e0e0', 
        py: 2,
        px: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: ATMANTAN_DARK_GOLD }}>
          Guest Itinerary
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, overflow: 'hidden', display: 'flex', height: '100%' }}>
        {renderContent()}
      </DialogContent>
    </Dialog>
  );
};

export default ItineraryModal;

